/**
 * 🛡️ PRATICANTES SERVICE - SERVIÇO DE GESTÃO DE PRATICANTES
 * 
 * @module PraticantesService
 * @description Gerencia operações CRUD e lógica de negócio para praticantes de Taekwondo
 * 
 * 🔧 CORREÇÕES IMPLEMENTADAS (06/12/2025):
 * - Adicionada sanitização automática de campos boolean em metadados
 * - Previne erro: "Unsupported field value: undefined (found in field metadados.active)"
 * - Aplica defaults seguros para campos undefined/null antes de qualquer operação no Firestore
 * - Sanitização presente em: create(), update(), updateView(), updatePropertyOnVinculo()
 * 
 * 📋 CAMPOS SANITIZADOS:
 * - active, federativeactive (default: false - security-first)
 * - aprovadoMestre, aprovadoFederacao (default: true - legacy compatibility)
 * - cancelado, blocked, desfiliado, bloqueioAdministrativo (default: false)
 * 
 * @see ValidarCamposBooleanPraticantes.jsx - Ferramenta administrativa para correção em lote
 */

import { db } from "../utils/firestore";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  getDocs,
  updateDoc,
  // deleteDoc,
  query,
  orderBy,
  where,
  // whereArrayContains,
  // startAfter,
  // endBefore,
  limit,
  setDoc,
  Timestamp,
  startAfter,
  runTransaction,
  arrayUnion,
} from "firebase/firestore";
// import { uniq } from "../utils/arrayUtils";
// import { TimerOutlined } from "@mui/icons-material";
import { generateTrigam, generateNormalizedTrigam } from "../utils/trigamUtils";
import { normalizarSearchableName } from "../utils/searchableName";
import { convertHistoricoGraduacoesValuesToTimestamp } from "../utils/HistoricoGraduacoesModeloAntigoUtil";
import { convert_historicoGraduacoesValuesToTimestamp } from "../utils/historicoGraduacoesUtil";
import GenericService from "./Generic.service";
import GenericVinculadoService from "./GenericVinculado.service";
//const dbCollection = db.collection("/praticantesgroups");
import axios from "axios";
import { UserModelo } from "../pages/security/users/models/user.modelo";
import UsersService from "./Users.Service";
import CPFIndexService, { CPF_INDEX_COLLECTION, getCpfDigits, formatCpf } from "./CPFIndex.service";
import { coalescerCadastro } from "../utils/coalescerCadastro";
import { criarCadastroUsuario, gruposDeVinculo } from "./GrupoPadrao.service";
import { proximaMatricula } from "../utils/proximaMatricula";

class praticantesService {

  /**
   * 🛡️ SANITIZAÇÃO DE METADADOS - PREVINE UNDEFINED EM CAMPOS BOOLEAN
   * 
   * Garante que todos os campos boolean em metadados tenham valores válidos
   * antes de salvar no Firestore. Isso previne o erro:
   * "Unsupported field value: undefined (found in field metadados.active)"
   * 
   * @param {Object} metadados - Objeto metadados a ser sanitizado
   * @returns {Object} Metadados com todos os campos boolean válidos
   */
  sanitizeMetadadosBooleanFields = (metadados = {}) => {
    const defaults = {
      active: false,                  // Security-first: desativado por padrão
      federativeactive: false,        // Security-first: desativado por padrão
      aprovadoMestre: true,          // Legacy compatibility
      aprovadoFederacao: true,       // Legacy compatibility
      cancelado: false,              // Estado normal
      blocked: false,
      desfiliado: false,
      bloqueioAdministrativo: false,
      exibirEmFiliados: true         // Por padrão, exibir o praticante
    };

    const sanitized = { ...metadados };

    // Aplica defaults apenas para campos undefined
    Object.keys(defaults).forEach(field => {
      if (sanitized[field] === undefined || sanitized[field] === null) {
        sanitized[field] = defaults[field];
      }
    });

    return sanitized;
  };

  getOneComCamposLimitados = async (collectionName, id) => {
    try {
      // console.log("GenericService.getOne(", collectionName, id, ")");
      if (id) {
        const noteSnapshot = await getDoc(doc(db, collectionName, id.toString()));
        if (noteSnapshot.exists()) {
          // console.log(collectionName, "id:", id, "=> noteSnapshot._document.version.timestamp.toDate()=", noteSnapshot._document.version.timestamp.toDate());
          // return noteSnapshot.data();
          const data = noteSnapshot.data();
          // console.log("getCollectionSearchActiveByPrefixLimitedFiliadoAEntidadeComGraduacaoVersion2ComCamposLimitados => data=", data);
          return {
            id: id,
            uid: id,
            name: data.dadosPessoais?.name.toUpperCase() || '',
            maxGraduacao: data.dadosTaekwondo?.maxGraduacao || 1,
            // MaxGraduacao: data.dadosTaekwondo?.MaxGraduacao || '',
            // cpf: data.dadosPessoais?.cpf || '',
            birthdate: data.dadosPessoais?.birthdate || '',
            sexo: data.dadosPessoais?.sexo || '',
            logradouroMunicipio: data.dadosEndereco?.logradouroMunicipio || '',
            logradouroUF: data.dadosEndereco?.logradouroUF || '',
            vinculadoAEntidade: data.dadosFiliacao?.vinculadoAEntidade || '',
            vinculadoAEntidadeID: data.dadosFiliacao?.vinculadoAEntidadeID || '',
            federacao: data.dadosFiliacao?.federacao || '',
            mestre: data.dadosTaekwondo?.mestre || '',
            mestreID: data.dadosTaekwondo?.mestreID || '',
            professor: data.dadosTaekwondo?.professor || '',
            professorID: data.dadosTaekwondo?.professorID || '',
            // email: data.dadosContato?.email || '',
            federativeactive: data.metadados?.federativeactive === undefined ? true : data.metadados?.federativeactive || 'false',
            active: data.metadados?.active || '',
            urlImagemID: data.dadosPessoais?.urlImagemID || '',
          };

        } else {
          throw new Error("Registro não encontrado! para o id: " + id + " na coleção: " + collectionName);
        }
      } else {
        return null;
      }
    } catch (error) {
      throw error;
    }
  };


  isNameRegistered = async (nameValue, mothersName) => {
    if (nameValue === undefined || nameValue === null || nameValue === "") return false;
    // Mesma normalização da escrita — ver client/src/utils/searchableName.js.
    nameValue = normalizarSearchableName(nameValue);
    // //console.log("isNameRegistered => nameValue=", nameValue);
    let querySnapshot = await getDocs(query(collection(db, "praticantes"), where("dadosPessoais.searchableName", "==", nameValue)));
    // //console.log("isNameRegistered => querySnapshot.docs.length=", querySnapshot.docs.length);
    let retValue = querySnapshot.docs.filter((doc) => doc.data().metadados.successorId === "");
    if (!retValue && mothersName !== undefined && mothersName !== null && mothersName !== "") {
      mothersName = mothersName.toLowerCase();
      retValue = querySnapshot.docs.filter((doc) => doc.data().dadosPessoais.mothersName.toLowerCase() === mothersName);
    }
    return retValue.length > 0
  };

  isCPFRegistered = async (cpfValue, id) => {
    if (cpfValue === undefined || cpfValue === null || cpfValue === "") return false;
    cpfValue = cpfValue.replace(/[^\d]+/g, "");
    //o valor do cpf precisa ser no formato "123.456.789-00", se não o Firestore não vai encontrar o registro
    if (cpfValue.length === 11) {
      cpfValue = cpfValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    console.log("[isCPFRegistered] 🔍 Verificando CPF:", cpfValue, "- ID para excluir:", id);
    let querySnapshot = await getDocs(query(collection(db, "praticantes"), where("dadosPessoais.cpf", "==", cpfValue), where("metadados.successorId", "==", "")));
    console.log("[isCPFRegistered] 📊 Documentos encontrados:", querySnapshot.docs.length);
    
    if (id !== undefined && id !== null && id !== "") {
      const filteredDocs = querySnapshot.docs.filter((doc) => {
        const shouldInclude = doc.data().metadados.successorId === "" && doc.id !== id;
        console.log(`[isCPFRegistered] Doc ${doc.id}: ${shouldInclude ? 'INCLUIR' : 'EXCLUIR'} (successorId: "${doc.data().metadados.successorId}", id match: ${doc.id === id})`);
        return shouldInclude;
      });
      console.log("[isCPFRegistered] ✅ Resultado após filtro:", filteredDocs.length > 0, "- Docs duplicados:", filteredDocs.length);
      return filteredDocs.length > 0;
    }
    const result = querySnapshot.docs.filter((doc) => doc.data().metadados.successorId === "").length > 0;
    console.log("[isCPFRegistered] ✅ Resultado (sem ID):", result);
    return result;
  };

  isCPFRegisteredRetornaIdExistenteOuVazioSeNaoTiver = async (cpfValue, id) => {
    if (cpfValue === undefined || cpfValue === null || cpfValue === "") return false;
    cpfValue = cpfValue.replace(/[^\d]+/g, "");
    //o valor do cpf precisa ser no formato "123.456.789-00", se não o Firestore não vai encontrar o registro
    if (cpfValue.length === 11) {
      cpfValue = cpfValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    let querySnapshot = await getDocs(query(collection(db, "praticantes"), where("dadosPessoais.cpf", "==", cpfValue), where("metadados.successorId", "==", "")));
    if (id !== undefined && id !== null && id !== "") {
      return querySnapshot.docs.filter((doc) => {
        return doc.data().metadados.successorId === "" && doc.id !== id;
      }).length > 0;
    }
    return querySnapshot.docs.filter((doc) => doc.data().metadados.successorId === "").length > 0 ? querySnapshot.docs.filter((doc) => doc.data().metadados.successorId === "")[0] : "";
  };

  isEmailRegistered = async (emailValue) => {
    // //console.log("isEmailRegistered => emailValue=", emailValue);
    if (emailValue === undefined || emailValue === null || emailValue === "") return false;
    emailValue = emailValue.toLowerCase();
    // //console.log("isEmailRegistered => emailValue=", emailValue);
    let querySnapshot = await getDocs(query(collection(db, "praticantes"), where("dadosContato.email", "==", emailValue)));
    // //console.log("isEmailRegistered => querySnapshot.docs.length=", querySnapshot.docs.length);
    return querySnapshot.docs.filter((doc) => doc.data().metadados.successorId === "").length > 0;
  };

  getEntidadesOndeEhResponsavelTecnicoFromPraticanteId = async (id) => {
    try {
      // Early return se id não for válido
      if (!id) {
        return [];
      }

      // Construir query diretamente sem array intermediário
      const querySnapshot = await getDocs(
        query(
          collection(db, "praticantes.dadosColaborador.administrativo"),
          where("uidPraticante", "==", id),
          where("metadados.active", "==", true),
          where("metadados.successorId", "==", "")
        )
      );

      // Verificação correta para querySnapshot vazio e mapeamento direto
      if (querySnapshot.empty) {
        return [];
      }

      // Usar map corretamente para extrair idEmpregador de forma mais eficiente
      return querySnapshot.docs.map(doc => doc.data().idEmpregador);

    } catch (error) {
      console.error("Error getting entidades onde eh responsavel tecnico:", error);
      return [];
    }
  }

  getEntidadesOndeEhFiliadoFromPraticanteId = async (id) => {
    try {
      // Early return se id não for válido
      if (!id) {
        return [];
      }
      // Construir query diretamente sem array intermediário
      const querySnapshot = await getDocs(
        query(
          collection(db, "praticantes.dadosFiliacao"),
          where("uidPraticante", "==", id),
          where("metadados.active", "==", true),
          where("metadados.successorId", "==", "")
        )
      );
      // Verificação correta para querySnapshot vazio e mapeamento direto
      if (querySnapshot.empty) {
        return [];
      }
      // Usar map corretamente para extrair idEmpregador de forma mais eficiente
      return querySnapshot.docs.map(doc => doc.data().vinculadoAEntidadeID);
    } catch (error) {

      console.error("Error getting entidades onde eh responsavel tecnico:", error);
      return [];
    }
  }

  getCPFFromPraticanteId = async (id) => {
    try {
      if (id) {
        const noteSnapshot = await getDoc(doc(db, "praticantes", id.toString()));
        if (noteSnapshot.exists()) {
          // //console.log("noteSnapshot.data()=", noteSnapshot.data());
          let praticante = noteSnapshot.data();
          // let praticante = await GenericService.getOne("praticantes", id);
          // if (praticante === null) return "Não informado";
          // //console.log("getCPFFromPraticanteId => praticante.dadosPessoais.cpf=", praticante.dadosPessoais.cpf ? praticante.dadosPessoais.cpf : "Não informado");
          return praticante.dadosPessoais.cpf ? praticante.dadosPessoais.cpf : "Não informado";
        } else {
          return "Não informado"
        }
      } else {
        return "Não informado";
      }
    } catch (error) {
      // throw error;
      //console.log(error);
      return "Não informado"
    }
  }

  getImageUrlFromPraticanteId = async (id) => {
    try {
      if (id) {
        const noteSnapshot = await getDoc(doc(db, "praticantes", id.toString()));
        if (noteSnapshot.exists()) {
          let praticante = noteSnapshot.data();
          return praticante.dadosPessoais.urlImage ? praticante.dadosPessoais.urlImage : praticante.dadosPessoais.urlImagemID ? praticante.dadosPessoais.urlImagemID : "";
        } else {
          return "Não informado"
        }
      } else {
        return "Não informado";
      }
    } catch (error) {
      // throw error;
      //console.log(error);
      return "Não informado"
    }
  }

  getMatriculaFromPraticanteId = async (id) => {    
    // Verifica se o ID é válido antes de prosseguir
    if (!id) return "Não informado";
    
    try {
      // Usa cache local para evitar consultas repetidas
      if (this._matriculaCache && this._matriculaCache[id]) {
        return this._matriculaCache[id];
      }
      
      // Busca os dados do praticante no Firestore
      const noteSnapshot = await getDoc(doc(db, "praticantes", id.toString()));
      
      // Verifica se o documento existe
      if (!noteSnapshot.exists()) return "Não informado";
      
      // Obtém os dados do praticante
      const praticante = noteSnapshot.data();
      
      // Obtém a matrícula ou retorna vazio se não existir
      let matricula = praticante.matriculaCBTKD || "";
      if (!matricula) return "Não informado";
      
      // Formatação da matrícula com base na graduação
      // Se não há graduação definida, retorna apenas a matrícula
      if (!praticante.dadosTaekwondo || praticante.dadosTaekwondo.maxGraduacao === undefined) {
        return matricula;
      }
      
      // Determina o prefixo com base na graduação (D para Dan, G para Gub)
      const graduacao = praticante.dadosTaekwondo.maxGraduacao;
      const prefixo = graduacao > 10
        ? "D" + Number(graduacao - 10)
        : "G" + Number(11 - graduacao);
      
      // Constrói a matrícula formatada
      const matriculaFormatada = prefixo + "-" + matricula;
      
      // Armazena em cache para futuras consultas
      if (!this._matriculaCache) this._matriculaCache = {};
      this._matriculaCache[id] = matriculaFormatada;
      
      return matriculaFormatada;
    } catch (error) {
      console.error("Erro ao buscar matrícula do praticante:", error);
      return "Não informado";
    }
  }

  /**
   * Método unificado que busca foto e matrícula em uma única consulta ao Firebase
   * @param {string} id - ID do praticante
   * @returns {Promise<Object>} - Objeto contendo foto e matrícula
   * 
   * Exemplo de uso:
   * const { foto, matricula } = await PraticantesService.getImageAndMatriculaFromPraticanteId("praticanteId");
   */
  getImageAndMatriculaFromPraticanteId = async (id) => {
    // Estrutura padrão de retorno
    const defaultReturn = {
      foto: "",
      matricula: "Não informado"
    };
    
    try {
      // Verifica se o ID é válido antes de prosseguir
      if (!id) {
        console.warn("getImageAndMatriculaFromPraticanteId: ID não fornecido");
        return defaultReturn;
      }
      
      // Verifica cache para matrícula (se existir)
      const cachedMatricula = this._matriculaCache && this._matriculaCache[id];
      
      // Busca os dados do praticante no Firestore (uma única consulta)
      const noteSnapshot = await getDoc(doc(db, "praticantes", id.toString()));

      
      // Verifica se o documento existe
      if (!noteSnapshot.exists()) {
        console.warn(`getImageAndMatriculaFromPraticanteId: Praticante ${id} não encontrado`);
        return defaultReturn;
      }
      
      // Obtém os dados do praticante
      const praticante = noteSnapshot.data();
      // console.log("getImageAndMatriculaFromPraticanteId: Iniciando busca para o praticante ID:", id, praticante);

      // === PROCESSAMENTO DA FOTO ===
      let foto = "";
      if (praticante.dadosPessoais) {
        // foto = praticante.dadosPessoais.urlImageID || praticante.dadosPessoais.urlImagem || "";
        foto = praticante.dadosPessoais.urlImagemID || "";
      }

      // === PROCESSAMENTO DA MATRÍCULA ===
      let matricula = "Não informado";
      
      // Se já tem cache, usa o cache
      if (cachedMatricula) {
        matricula = cachedMatricula;
      } else {
        // Processa a matrícula
        const matriculaBruta = praticante.matriculaCBTKD;
        if (matriculaBruta) {
          // Formatação da matrícula com base na graduação
          if (praticante.dadosTaekwondo && praticante.dadosTaekwondo.maxGraduacao !== undefined) {
            const graduacao = praticante.dadosTaekwondo.maxGraduacao;
            const prefixo = graduacao > 10
              ? "D" + Number(graduacao - 10)
              : "G" + Number(11 - graduacao);
            
            matricula = prefixo + "-" + matriculaBruta;
          } else {
            matricula = matriculaBruta.toString();
          }
          
          // Armazena em cache para futuras consultas
          if (!this._matriculaCache) this._matriculaCache = {};
          this._matriculaCache[id] = matricula;
        }
      }

      return {
        foto,
        matricula
      };

    } catch (error) {
      console.error(`Erro ao buscar foto e matrícula do praticante ${id}:`, error);
      return defaultReturn;
    }
  }

  getHistoricoGraduacoes = async (id) => {
    try {
      if (id) {
        let historicoGraduacoesVazio = ["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", ""];
        const noteSnapshot = await getDoc(doc(db, "praticantes", id.toString()));
        if (noteSnapshot.exists()) {
          // //console.log("noteSnapshot.data()=", noteSnapshot.data());
          let praticante = noteSnapshot.data();
          // let praticante = await GenericService.getOne("praticantes", id);
          // if (praticante === null) return "Não informado";
          // //console.log("getCPFFromPraticanteId => praticante.dadosPessoais.cpf=", praticante.dadosPessoais.cpf ? praticante.dadosPessoais.cpf : "Não informado");
          return praticante.dadosTaekwondo !== undefined ? praticante.dadosTaekwondo.HistoricoGraduacoes !== undefined ? praticante.dadosTaekwondo.HistoricoGraduacoes : historicoGraduacoesVazio : historicoGraduacoesVazio;
        } else {
          return historicoGraduacoesVazio;
        }
      } else {
        return "Não informado";
      }
    } catch (error) {
      // throw error;
      //console.log(error);
      return "Não informado"
    }
  }

  /**
   * 🥋 SALVA APENAS O HISTÓRICO DE GRADUAÇÃO DO PRATICANTE
   *
   * Salva exclusivamente o ramo de graduação (HistoricoGraduacoes + historicoGraduacoes)
   * dentro de dadosTaekwondo, preservando:
   *  - todos os demais campos de dadosTaekwondo (registroWT, kukkiwon, mestre, etc.);
   *  - todos os demais ramos do cadastro (dadosPessoais, dadosContato, etc.);
   *  - os metadados (owner, creation) e o versionamento padrão do sistema.
   *
   * ⚠️ REGRA CRÍTICA: o array vai SEMPRE COMPLETO (19 posições). O merge:true do
   * Firestore SUBSTITUI arrays inteiros — itens omitidos seriam perdidos.
   *
   * @param {string} id - ID do praticante (documento deve existir).
   * @param {Object} payload
   * @param {Array} payload.HistoricoGraduacoes - array legado (19 posições, modelo antigo).
   * @param {Array} payload.historicoGraduacoes  - array novo (19 posições, modelo novo).
   * @returns {Promise} resultado de GenericService.update.
   */
  saveHistoricoGraduacoes = async (id, { HistoricoGraduacoes, historicoGraduacoes }) => {
    if (!id) {
      throw new Error("saveHistoricoGraduacoes: id do praticante é obrigatório.");
    }
    if (!Array.isArray(HistoricoGraduacoes) || HistoricoGraduacoes.length !== 19) {
      throw new Error(
        `saveHistoricoGraduacoes: HistoricoGraduacoes deve ter exatamente 19 posições. ` +
        `Recebido: ${Array.isArray(HistoricoGraduacoes) ? HistoricoGraduacoes.length : typeof HistoricoGraduacoes}.`
      );
    }
    if (!Array.isArray(historicoGraduacoes) || historicoGraduacoes.length !== 19) {
      throw new Error(
        `saveHistoricoGraduacoes: historicoGraduacoes deve ter exatamente 19 posições. ` +
        `Recebido: ${Array.isArray(historicoGraduacoes) ? historicoGraduacoes.length : typeof historicoGraduacoes}.`
      );
    }

    // 1. Documento FRESCO do Firestore — garante que apenas o ramo de graduação seja
    //    sobreposto e que edições não-salvas de outros campos não sejam persistidas.
    const original = await GenericService.getOne("praticantes", id);
    if (!original) {
      throw new Error(`saveHistoricoGraduacoes: praticante ${id} não encontrado.`);
    }

    // 2. Normalizar timestamps antes de gravar (modelos antigo e novo).
    const HistoricoGraduacoesTimestamp = convertHistoricoGraduacoesValuesToTimestamp(HistoricoGraduacoes);
    const historicoGraduacoesTimestamp = convert_historicoGraduacoesValuesToTimestamp(historicoGraduacoes);

    // 3. Spread completo + sobreposição apenas do ramo de graduação.
    const updates = {
      ...original,
      dadosTaekwondo: {
        ...original.dadosTaekwondo,
        HistoricoGraduacoes: HistoricoGraduacoesTimestamp,
        historicoGraduacoes: historicoGraduacoesTimestamp,
      },
      // 4. Preservar metadados (owner, creation). version/successorId são tratados
      //    pelo GenericService.update.
      metadados: {
        ...original.metadados,
      },
    };

    // 5. Versionamento automático.
    return await GenericService.update("praticantes", id, updates);
  }

  getNacionalidadeComPraticanteId = async (id) => {
    try {
      if (id) {
        const noteSnapshot = await getDoc(doc(db, "praticantes", id.toString()));
        if (noteSnapshot.exists()) {
          // //console.log("noteSnapshot.data()=", noteSnapshot.data());
          let praticante = noteSnapshot.data();
          // let praticante = await GenericService.getOne("praticantes", id);
          // if (praticante === null) return "Não informado";
          // //console.log("getCPFFromPraticanteId => praticante.dadosPessoais.cpf=", praticante.dadosPessoais.cpf ? praticante.dadosPessoais.cpf : "Não informado");
          return praticante.dadosPessoais.nacionalidade ? praticante.dadosPessoais.nacionalidade : "Brasil";
        } else {
          return "Brasil"
        }
      } else {
        return "Brasil";
      }
    } catch (error) {
      // throw error;
      //console.log(error);
      return "Brasil"
    }
  }

  getNacionalidadeEHisoricoGraduacoesComPraticanteId = async (id) => {
    try {
      if (id) {
        const noteSnapshot = await getDoc(doc(db, "praticantes", id.toString()));
        if (noteSnapshot.exists()) {
          // //console.log("noteSnapshot.data()=", noteSnapshot.data());
          let praticante = noteSnapshot.data();
          // let praticante = await GenericService.getOne("praticantes", id);
          // if (praticante === null) return "Não informado";
          // //console.log("getCPFFromPraticanteId => praticante.dadosPessoais.cpf=", praticante.dadosPessoais.cpf ? praticante.dadosPessoais.cpf : "Não informado");
          return {
            nacionalidade: praticante.dadosPessoais.nacionalidade ? praticante.dadosPessoais.nacionalidade : "Brasil",
            historicoGraduacoes: praticante.dadosTaekwondo !== undefined ? praticante.dadosTaekwondo.HistoricoGraduacoes !== undefined ? praticante.dadosTaekwondo.HistoricoGraduacoes : ["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", ""] : ["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", ""]
          };
        } else {
          return {
            nacionalidade: "Brasil",
            historicoGraduacoes: ["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", ""]
          }
        }
      } else {
        return {
          nacionalidade: "Brasil",
          historicoGraduacoes: ["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", ""]
        };
      }
    } catch (error) {
      // throw error;
      //console.log(error);
      return {
        nacionalidade: "Brasil",
        historicoGraduacoes: ["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", ""]
      }
    }
  }


  getCollectionSearchActiveByPrefixLimitedFiliadoAEntidadeMinimalGraduacao = async (
    queryTarget,
    queryText,
    queryLimit,
    onlyActive,
    showAllVersions,
    dadosFiliacao_federacaoNome,
    dadosTaekwondo_graduacao
  ) => {
    //console.log(queryTarget, queryText, queryLimit, onlyActive, showAllVersions, dadosFiliacao_federacaoNome, dadosTaekwondo_graduacao);
    let conditions = await GenericVinculadoService.getConditionsFromQueryFilter(queryTarget, queryText);
    conditions.push(where("dadosFiliacao.federacao", "==", dadosFiliacao_federacaoNome));
    if (onlyActive) conditions.push(where("metadados.active", "==", true));
    if (!showAllVersions) conditions.push(where("metadados.successorId", "==", ""));
    //console.log("conditions=", conditions);
    let querySnapshot = await getDocs(query(collection(db, "praticantes"), ...conditions, orderBy(queryTarget, "asc"), limit(queryLimit)));

    let filteredDocs = [];
    if (dadosTaekwondo_graduacao) {
      querySnapshot.docs.forEach((doc) => {
        if (doc.data().dadosTaekwondo.maxGraduacao >= dadosTaekwondo_graduacao) {
          filteredDocs.push(doc);
        }
      });
    } else {
      filteredDocs = querySnapshot.docs;
    }
    return filteredDocs;
  };

  getCollectionSearchActiveByPrefixLimitedFiliadoAEntidadeComGraduacao = async (
    queryTarget,
    queryText,
    queryLimit,
    onlyActive,
    showAllVersions,
    dadosFiliacao_federacaoNome,
    dadosTaekwondo_graduacaoLista,
    dadosFiliacao_vinculadoAEntidadeNome,
    lastVisible,
    operation
  ) => {
    // console.log(
    //   "queryTarget=",
    //   queryTarget,
    //   " queryText=",
    //   queryText,
    //   " queryLimit=",
    //   queryLimit,
    //   " showAllVersions=",
    //   showAllVersions,
    //   "dadosFiliacao_federacaoNome=",
    //   dadosFiliacao_federacaoNome,
    //   "dadosTaekwondo_graduacaoLista=",
    //   dadosTaekwondo_graduacaoLista,
    //   "dadosFiliacao_vinculadoAEntidadeNome=",
    //   dadosFiliacao_vinculadoAEntidadeNome,
    //   " lastVisible=",
    //   lastVisible,
    //   "operation=",
    //   operation
    // );

    let conditions = [];
    if (queryText.length > 0) conditions = await GenericService.getConditionsFromQueryFilter(queryTarget, queryText);
    conditions.push(where("dadosFiliacao.federacao", "==", dadosFiliacao_federacaoNome));
    if (onlyActive) conditions.push(where("metadados.active", "==", true));
    if (!showAllVersions) conditions.push(where("metadados.successorId", "==", ""));
    if (
      (operation === "not-in" && dadosTaekwondo_graduacaoLista.length > 0) ||
      (operation === "in" && dadosTaekwondo_graduacaoLista.length <= 10 && dadosTaekwondo_graduacaoLista.length > 0)
    ) {
      // //console.log("where(dadosTaekwondo.maxGraduacao", operation, dadosTaekwondo_graduacaoLista, ");");
      conditions.push(where("dadosTaekwondo.maxGraduacao", operation, dadosTaekwondo_graduacaoLista));
    }
    if (operation === "not-in") {
      conditions.push(orderBy("dadosTaekwondo.maxGraduacao", "asc"));
    }

    if (dadosFiliacao_vinculadoAEntidadeNome)
      conditions.push(where("dadosFiliacao.vinculadoAEntidade", "==", dadosFiliacao_vinculadoAEntidadeNome));
    conditions.push(orderBy(queryTarget, "asc"));
    if (queryLimit !== undefined && queryLimit !== null && queryLimit !== "") conditions.push(limit(queryLimit));
    if (lastVisible !== undefined && lastVisible !== null && lastVisible !== "") conditions.push(startAfter(lastVisible));
    // console.log("conditions=", conditions);
    let querySnapshot = await getDocs(query(collection(db, "praticantes"), ...conditions));
    return querySnapshot;
    // let filteredDocs = [];
    // if (dadosTaekwondo_graduacaoLista) {
    //   querySnapshot.docs.forEach((doc) => {
    //     if (doc.data().dadosTaekwondo.maxGraduacao in dadosTaekwondo_graduacaoLista) {
    //       filteredDocs.push(doc);
    //     }
    //   });
    // } else {
    //   filteredDocs = querySnapshot.docs;
    // }
    // return filteredDocs;
  };


  getCollectionSearchActiveByPrefixLimitedFiliadoAEntidadeComGraduacaoVersion2 = async (
    queryTarget,
    queryText,
    queryLimit,
    onlyActive,
    showAllVersions,
    dadosFiliacao_federacaoNome,
    // dadosTaekwondo_graduacaoLista,
    dadosFiliacao_vinculadoAEntidadeNome,
    lastVisible,
    operation
  ) => {
    let conditions = [];
    if (queryText.length > 0) conditions = await GenericService.getConditionsFromQueryFilter(queryTarget, queryText);
    conditions.push(where("dadosFiliacao.federacao", "==", dadosFiliacao_federacaoNome));
    if (onlyActive) conditions.push(where("metadados.active", "==", true));
    if (!showAllVersions) conditions.push(where("metadados.successorId", "==", ""));
    // if (
    //   (operation === "not-in" && dadosTaekwondo_graduacaoLista.length > 0) ||
    //   (operation === "in" && dadosTaekwondo_graduacaoLista.length <= 10 && dadosTaekwondo_graduacaoLista.length > 0)
    // ) {
    //   // //console.log("where(dadosTaekwondo.maxGraduacao", operation, dadosTaekwondo_graduacaoLista, ");");
    //   conditions.push(where("dadosTaekwondo.maxGraduacao", operation, dadosTaekwondo_graduacaoLista));
    // }
    // if (operation === "not-in") {
    //   conditions.push(orderBy("dadosTaekwondo.maxGraduacao", "asc"));
    // }

    if (dadosFiliacao_vinculadoAEntidadeNome)
      conditions.push(where("dadosFiliacao.vinculadoAEntidade", "==", dadosFiliacao_vinculadoAEntidadeNome));
    conditions.push(orderBy(queryTarget, "asc"));
    if (queryLimit !== undefined && queryLimit !== null && queryLimit !== "") conditions.push(limit(queryLimit));
    if (lastVisible !== undefined && lastVisible !== null && lastVisible !== "") conditions.push(startAfter(lastVisible));
    // console.log("conditions=", conditions);
    let querySnapshot = await getDocs(query(collection(db, "praticantes"), ...conditions));
    return querySnapshot;
    // let filteredDocs = [];
    // if (dadosTaekwondo_graduacaoLista) {
    //   querySnapshot.docs.forEach((doc) => {
    //     if (doc.data().dadosTaekwondo.maxGraduacao in dadosTaekwondo_graduacaoLista) {
    //       filteredDocs.push(doc);
    //     }
    //   });
    // } else {
    //   filteredDocs = querySnapshot.docs;
    // }
    // return filteredDocs;
  };


  getCollectionSearchActiveByPrefixLimitedFiliadoAEntidadeComGraduacaoVersion2ComCamposLimitados = async (
    queryTarget,
    queryText,
    queryLimit,
    onlyActive,
    showAllVersions,
    dadosFiliacao_federacaoNome,
    // dadosTaekwondo_graduacaoLista,
    dadosFiliacao_vinculadoAEntidadeNome,
    lastVisible,
    operation
  ) => {
    let conditions = [];
    if (queryText.length > 0) conditions = await GenericService.getConditionsFromQueryFilter(queryTarget, queryText);
    conditions.push(where("dadosFiliacao.federacao", "==", dadosFiliacao_federacaoNome));
    if (onlyActive) conditions.push(where("metadados.active", "==", true));
    if (!showAllVersions) conditions.push(where("metadados.successorId", "==", ""));
    if (dadosFiliacao_vinculadoAEntidadeNome && dadosFiliacao_vinculadoAEntidadeNome !== "") {
      conditions.push(where("dadosFiliacao.vinculadoAEntidade", "==", dadosFiliacao_vinculadoAEntidadeNome));
    }
    conditions.push(orderBy(queryTarget, "asc"));
    if (queryLimit !== undefined && queryLimit !== null && queryLimit !== "") conditions.push(limit(queryLimit));
    if (lastVisible !== undefined && lastVisible !== null && lastVisible !== "") conditions.push(startAfter(lastVisible));
    // console.log("conditions=", conditions);
    let querySnapshot = await getDocs(query(collection(db, "praticantes"), ...conditions));
    // console.log("getCollectionSearchActiveByPrefixLimitedFiliadoAEntidadeComGraduacaoVersion2 => querySnapshot=", querySnapshot);
    // console.log("getCollectionSearchActiveByPrefixLimitedFiliadoAEntidadeComGraduacaoVersion2 => querySnapshot.docs.length=", querySnapshot.docs.length);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      // console.log("getCollectionSearchActiveByPrefixLimitedFiliadoAEntidadeComGraduacaoVersion2ComCamposLimitados => data=", data);
      return {
        id: doc.id,
        uid: doc.id,
        name: data.dadosPessoais?.name.toUpperCase() || '',
        maxGraduacao: data.dadosTaekwondo?.maxGraduacao || 1,
        // MaxGraduacao: data.dadosTaekwondo?.MaxGraduacao || '',
        // cpf: data.dadosPessoais?.cpf || '',
        birthdate: data.dadosPessoais?.birthdate || '',
        sexo: data.dadosPessoais?.sexo || '',
        logradouroMunicipio: data.dadosEndereco?.logradouroMunicipio || '',
        logradouroUF: data.dadosEndereco?.logradouroUF || '',
        vinculadoAEntidade: data.dadosFiliacao?.vinculadoAEntidade || '',
        vinculadoAEntidadeID: data.dadosFiliacao?.vinculadoAEntidadeID || '',
        federacao: data.dadosFiliacao?.federacao || '',
        mestre: data.dadosTaekwondo?.mestre || '',
        mestreID: data.dadosTaekwondo?.mestreID || '',
        professor: data.dadosTaekwondo?.professor || '',
        professorID: data.dadosTaekwondo?.professorID || '',
        // email: data.dadosContato?.email || '',
        federativeactive: data.metadados?.federativeactive || '',
        active: data.metadados?.active || '',
        urlImagemID: data.dadosPessoais?.urlImagemID || '',
      };
    });
    // return querySnapshot;
  };

  /** Maior matrícula numérica + 1 — ver `utils/proximaMatricula.js` (era count + 5000). */
  newID = () => proximaMatricula(db);

  create = async (value) => {
    try {
      // value = { ...value, matriculaCBTKD: await this.newID(), dadosPessoais: { ...value.dadosPessoais, trigam: generateTrigam(value.dadosPessoais.name) } };
      // return await addDoc(collection(db, "praticantes"), value);
      return await this.createWithID(null, value);
    } catch (error) {
      throw error;
    }
  };

  /**
   * Cria um praticante com transaction para garantir atomicidade
   * Verifica CPF duplicado e cria o registro em uma única transação
   * 
   * @param {string} UID - ID do usuário
   * @param {Object} value - Dados do praticante
   * @param {boolean} doNotCreateAuthenticationUser - Flag para não criar usuário de autenticação
   * @returns {Promise<string>} - ID do praticante criado
   * @throws {Error} - Se CPF já existe ou erro na transação
   */
  createWithIDTransaction = async (UID, value, doNotCreateAuthenticationUser) => {
    try {
      console.log("[PraticantesService] createWithIDTransaction iniciando...");
      
      // Normaliza o CPF para o formato padrão (XXX.XXX.XXX-XX)
      let cpfFormatado = value.dadosPessoais.cpf;
      if (cpfFormatado) {
        const cpfDigitos = cpfFormatado.replace(/\D/g, '');
        if (cpfDigitos.length === 11) {
          cpfFormatado = cpfDigitos.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
        }
      }
      
      // 1. Verificar CPF duplicado ANTES da transação (getDocs não funciona dentro de transaction)
      const cpfQuery = query(
        collection(db, "praticantes"),
        where("dadosPessoais.cpf", "==", cpfFormatado),
        where("metadados.successorId", "==", "")
      );
      
      const cpfSnapshot = await getDocs(cpfQuery);
      console.log("[PraticantesService] Verificação de CPF - encontrados:", cpfSnapshot.size);
      
      // Se UID foi fornecido, filtra para ignorar o próprio registro
      const cpfExistente = cpfSnapshot.docs.filter(doc => {
        if (UID) {
          return doc.id !== UID;
        }
        return true;
      });
      
      if (cpfExistente.length > 0) {
        throw new Error("CPF já cadastrado no sistema. Use o cadastro existente ou entre em contato com o suporte.");
      }
      
      // Executar transação atômica
      const result = await runTransaction(db, async (transaction) => {
        console.log("[PraticantesService] Transaction iniciada");

        // 🛡️ ÍNDICE ÚNICO DE CPF: lê antes de qualquer escrita (regra do Firestore)
        const cpfDigitsTx = getCpfDigits(cpfFormatado);
        let cpfIndexRef = null;
        if (cpfDigitsTx) {
          cpfIndexRef = doc(db, CPF_INDEX_COLLECTION, cpfDigitsTx);
          const cpfIndexSnap = await transaction.get(cpfIndexRef);
          if (cpfIndexSnap.exists()) {
            const existing = cpfIndexSnap.data();
            if (existing.praticanteId && existing.praticanteId !== UID) {
              throw new Error(
                "CPF já cadastrado no sistema. Use o cadastro existente ou entre em contato com o suporte."
              );
            }
          }
        }

        // 2. Gerar matrícula
        const matricula = await this.newID();
        console.log("[PraticantesService] Matrícula gerada:", matricula);
        
        // 3. Preparar dados com trigramas
        const nomeCompleto = value.dadosPessoais.name || "Nome não disponível";
        const trigamValue = generateTrigam(nomeCompleto);
        const normalizedTrigamValue = Array.from(generateNormalizedTrigam(nomeCompleto));
        const searchWords = this.generateSearchWords(nomeCompleto);
        
        const dadosCompletos = {
          ...value,
          matriculaCBTKD: matricula,
          dadosPessoais: {
            ...value.dadosPessoais,
            cpf: cpfFormatado, // Garante formato padrão
            name: nomeCompleto.toUpperCase(),
            searchableName: normalizarSearchableName(nomeCompleto),
            trigam: trigamValue,
            normalizedtrigam: normalizedTrigamValue,
            searchWords: searchWords
          },
          metadados: {
            ...value.metadados,
            creation: value.metadados.creation || Timestamp.fromDate(new Date()),
            owner: value.metadados.owner || nomeCompleto
          }
        };
        
        // 4. Criar o documento dentro da transação
        const docRef = doc(db, "praticantes", UID.toString());
        transaction.set(docRef, dadosCompletos);

        // 🛡️ ÍNDICE ÚNICO DE CPF: cria atomicamente junto ao praticante
        if (cpfIndexRef) {
          transaction.set(cpfIndexRef, {
            praticanteId: UID.toString(),
            cpfFormatado: formatCpf(cpfDigitsTx),
            ownerid: value.metadados?.ownerid || null,
            createdAt: Timestamp.fromDate(new Date()),
          });
        }

        console.log("[PraticantesService] Praticante criado na transaction:", UID);
        return UID;
      });
      
      // 5. Operações APÓS a transação (não bloqueantes)
      console.log("[PraticantesService] Transaction concluída, executando pós-processamento");
      
      // Criar usuário de autenticação (se necessário)
      if (!doNotCreateAuthenticationUser) {
        try {
          {
            // Criar documento em users
            let newUser = UserModelo({ uid: value.metadados.ownerid, displayName: value.metadados.owner });
            const userSearchWords = this.generateSearchWords(value.dadosPessoais.name);
            
            newUser = {
              ...newUser,
              dadosPessoais: {
                ...newUser.dadosPessoais,
                name: value.dadosPessoais.name,
                searchableName: normalizarSearchableName(value.dadosPessoais.name),
                trigam: generateTrigam(value.dadosPessoais.name),
                searchWords: userSearchWords
              }
            };
            
            // P13: `users` não é mais gravado do cliente. A callable
            // `criarCadastroUsuario` cria a conta no Auth E o documento, numa
            // transação, com `metadados` ditado pelo servidor — inclusive
            // `successorId: ""`, cuja ausência tornava o cadastro invisível nas
            // listagens. Ver functions/cadastroUsuario.js.
            await criarCadastroUsuario({ users: newUser, uid: UID.toString(), criarPraticante: false });
            console.log("[PraticantesService] Usuário de autenticação criado");
          }
        } catch (authError) {
          console.error("[PraticantesService] Erro ao criar usuário de autenticação (não crítico):", authError);
          // Não falha a operação se houver erro na criação do usuário
        }
      }
      
      // Atualizar view
      try {
        await this.updateView(UID);
        console.log("[PraticantesService] View atualizada");
      } catch (viewError) {
        console.error("[PraticantesService] Erro ao atualizar view (não crítico):", viewError);
      }
      
      console.log("[PraticantesService] createWithIDTransaction concluído com sucesso");
      return result;
      
    } catch (error) {
      console.error("[PraticantesService] Erro em createWithIDTransaction:", error);
      throw error;
    }
  };

  /**
   * 🎯 REGRA CRÍTICA: Sincronização de IDs
   * =====================================
   * Firebase Auth UID = users Doc ID = praticantes Doc ID
   * 
   * Este método cria um praticante com transaction e, se houver email,
   * cria também o Auth User e o documento users COM O MESMO ID.
   * 
   * FLUXO:
   * 1. Transaction: Cria documento em praticantes collection (gera ID automático)
   * 2. Post-transaction: Cria Auth User no Firebase Authentication com o MESMO ID
   * 3. Post-transaction: Cria documento em users collection com o MESMO ID
   * 4. Post-transaction: Atualiza view
   * 
   * VALIDAÇÃO:
   * - O método verifica se returnedUid === createdId e registra erro se divergirem
   * - Logs detalhados facilitam debug de problemas de sincronização
   * 
   * @param {Object} praticanteData - Dados completos do praticante
   * @param {boolean} skipExternalValidation - Se true, pula validação externa (hubDiv)
   * @returns {Promise<Object>} - { id: string } - ID único usado em todos os lugares
   * @throws {Error} - Se CPF já existe ou erro na transação
   */
  // Coalescido pela identidade do praticante. A verificação de duplicidade abaixo
  // (`getDocs` por CPF, ou por nome quando não há CPF) roda ANTES da transação —
  // o próprio comentário do código diz que getDocs não funciona dentro dela. Dois
  // cliques passam ambos por essa consulta antes de qualquer um gravar, e nascem
  // dois praticantes, duas contas no Auth e dois documentos `users`.
  //
  // Chave: SÓ o CPF. Cair para o e-mail seria perigoso aqui — ao contrário do
  // Auth, nada garante e-mail único em `praticantes`, e dois irmãos que
  // compartilham o e-mail do responsável coalesceriam num cadastro só.
  //
  // A IMPORTAÇÃO EM LOTE fica de fora (`skipExternalValidation`): ela aceita
  // registro sem CPF (ver ImportarDadosPraticantes.jsx, linha 88), é um laço
  // controlado e não tem duplo clique. Agrupar ali só criaria o risco de sumir
  // com uma linha da planilha em silêncio.
  //
  // `opcoes.criarLogin: false` pula a criação de Auth + `users` aqui: a importação
  // por planilha cria o trio depois, no servidor (/api/importacao-logins), com
  // e-mail fictício quando falta e grupo no portal da federação.
  createSimplifiedTransaction = async (praticanteData, skipExternalValidation = false, opcoes = {}) =>
    coalescerCadastro(
      skipExternalValidation === true
        ? ""
        : `praticante:${(praticanteData?.dadosPessoais?.cpf || "").replace(/\D/g, "")}`,
      async () => {
    try {
      const isImportacao = skipExternalValidation === true;
      console.log(`[PraticantesService] createSimplifiedTransaction iniciando... ${isImportacao ? '(MODO IMPORTAÇÃO - sem validação externa)' : ''}`);
      
      // Normaliza o CPF
      let cpfFormatado = praticanteData.dadosPessoais.cpf;
      let temCPF = false; // Flag para indicar se tem CPF válido
      
      if (cpfFormatado) {
        const cpfDigitos = cpfFormatado.replace(/\D/g, '');
        if (cpfDigitos.length === 11) {
          cpfFormatado = cpfDigitos.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
          temCPF = true;
        } else if (cpfDigitos.length > 0 && cpfDigitos.length < 11) {
          // CPF incompleto - tratar como vazio
          console.log(`⚠️ [PraticantesService] CPF incompleto (${cpfDigitos.length} dígitos): ${cpfDigitos} - será tratado como vazio`);
          cpfFormatado = "";
          temCPF = false;
        } else {
          // CPF vazio ou inválido
          cpfFormatado = "";
          temCPF = false;
        }
      } else {
        cpfFormatado = "";
        temCPF = false;
      }
      
      let createdId = null;
      
      // 1. Verificar duplicidade ANTES da transação (getDocs não funciona dentro de transaction)
      // 🎯 ESTRATÉGIA: CPF válido → valida por CPF | CPF vazio → valida por NOME
      if (temCPF) {
        // ===== VALIDAÇÃO POR CPF =====
        // Sem o CPF no log em importação: uma planilha de milhares de linhas
        // despejaria a base inteira no console da aba (invariante §6.4 do plano).
        console.log(
          isImportacao
            ? "[PraticantesService] CPF válido - validando duplicidade por CPF..."
            : `[PraticantesService] ✅ CPF válido detectado: ${cpfFormatado} - validando duplicidade por CPF...`,
        );
        
        const cpfQuery = query(
          collection(db, "praticantes"),
          where("dadosPessoais.cpf", "==", cpfFormatado),
          where("metadados.successorId", "==", ""),
          limit(10) // Limite por segurança: 1 normal + até 5 duplicados históricos + margem
        );
        
        const cpfSnapshot = await getDocs(cpfQuery);
        console.log("[PraticantesService] Verificação de CPF - encontrados:", cpfSnapshot.size);
        
        if (isImportacao) {
          console.log("⚠️ [IMPORTAÇÃO] Validação externa de CPF desabilitada (hubDiv não será consultado)");
        }
        
        if (cpfSnapshot.docs.length > 0) {
          const existente = cpfSnapshot.docs[0];
          const praticante = existente.data();
          const nomePraticante = praticante.dadosPessoais?.name || "Nome não informado";
          const federacao = praticante.dadosFiliacao?.federacao || "Federação não informada";
          const uf = praticante.dadosEndereco?.logradouroUF || "UF não informada";

          const err = new Error(
            `CPF já cadastrado!\n\n` +
            `Praticante: ${nomePraticante}\n` +
            `Federação: ${federacao}\n` +
            `UF: ${uf}\n\n` +
            `ID: ${existente.id}`
          );
          err.code = "CPF_DUPLICADO";
          err.cpfDuplicado = {
            cpf: cpfFormatado,
            cpfDigits: cpfFormatado.replace(/\D/g, ""),
            praticanteId: existente.id,
            nome: nomePraticante,
            federacao,
            uf,
            entidade: praticante.dadosFiliacao?.vinculadoAEntidade || "",
            entidadeID: praticante.dadosFiliacao?.vinculadoAEntidadeID || "",
            dataNascimento:
              praticante.dadosPessoais?.dataNascimentoCPF ||
              praticante.dadosPessoais?.birthdate ||
              praticante.dadosPessoais?.dataNascimento || "",
          };
          throw err;
        }
      } else {
        // ===== VALIDAÇÃO POR NOME (quando não há CPF) =====
        console.log(`⚠️ [PraticantesService] CPF vazio/inválido - validando duplicidade por NOME...`);
        
        const nomeCompleto = praticanteData.dadosPessoais?.name || "";
        if (nomeCompleto.trim() !== "") {
          // Mesma normalização da ESCRITA de searchableName. Sem ela esta checagem
          // fica cega para nomes com acento e passa a aceitar duplicatas.
          const nomeNormalizado = normalizarSearchableName(nomeCompleto);
          // Sem o nome em importação: linha sem CPF é caso normal (D3), então
          // isto imprimiria a planilha inteira no console (invariante §6.4).
          if (!isImportacao) console.log(`   🔍 Buscando por nome: "${nomeNormalizado}"`);
          
          const nomeQuery = query(
            collection(db, "praticantes"),
            where("dadosPessoais.searchableName", "==", nomeNormalizado),
            where("metadados.successorId", "==", ""),
            limit(10) // Limite por segurança: 1 normal + até 5 duplicados históricos + margem
          );
          
          const nomeSnapshot = await getDocs(nomeQuery);
          console.log(`[PraticantesService] Verificação de NOME - encontrados: ${nomeSnapshot.size}`);
          
          if (nomeSnapshot.docs.length > 0) {
            // Encontrou nome duplicado - verificar se é da mesma federação
            const federacaoAtual = praticanteData.dadosFiliacao?.federacao || "";
            const existente = nomeSnapshot.docs[0];
            const praticante = existente.data();
            const federacaoExistente = praticante.dadosFiliacao?.federacao || "";
            
            if (federacaoAtual === federacaoExistente) {
              // Mesma federação = provável duplicata
              const nomePraticante = praticante.dadosPessoais?.name || "Nome não informado";
              const uf = praticante.dadosEndereco?.logradouroUF || "UF não informada";
              
              console.warn(`⚠️ [PraticantesService] POSSÍVEL DUPLICATA detectada (mesmo nome + mesma federação)`);
              if (!isImportacao) console.warn(`   Nome: ${nomePraticante}`);
              console.warn(`   Federação: ${federacaoExistente}`);
              console.warn(`   ID existente: ${existente.id}`);
              
              throw new Error(
                `Nome já cadastrado na mesma federação!\n\n` +
                `Praticante: ${nomePraticante}\n` +
                `Federação: ${federacaoExistente}\n` +
                `UF: ${uf}\n\n` +
                `ID: ${existente.id}\n\n` +
                `⚠️ Cadastros sem CPF só são permitidos se forem de federações diferentes.`
              );
            } else {
              // Federações diferentes = permitir (homônimos)
              console.log(`   ✅ Nome duplicado mas de FEDERAÇÃO DIFERENTE - permitindo cadastro`);
              console.log(`   Federação existente: ${federacaoExistente} | Nova: ${federacaoAtual}`);
            }
          } else {
            console.log(`   ✅ Nome não encontrado - cadastro permitido`);
          }
        } else {
          console.warn(`   ⚠️ Nome vazio - cadastro será criado SEM validação (situação anormal)`);
        }
        
        console.log(`   📌 Cadastro será criado SEM CPF (permitido em modo importação)`);
      }
      
      // Executar transação atômica
      await runTransaction(db, async (transaction) => {
        console.log("[PraticantesService] Transaction iniciada (simplified)");

        // 🛡️ ÍNDICE ÚNICO DE CPF: leitura antes de qualquer escrita
        const cpfDigitsTx = getCpfDigits(cpfFormatado);
        let cpfIndexRef = null;
        if (cpfDigitsTx) {
          cpfIndexRef = doc(db, CPF_INDEX_COLLECTION, cpfDigitsTx);
          const cpfIndexSnap = await transaction.get(cpfIndexRef);
          if (cpfIndexSnap.exists()) {
            const existing = cpfIndexSnap.data() || {};
            const err = new Error(
              `CPF já cadastrado!\n\nO CPF ${cpfFormatado} já possui registro ativo no sistema.`
            );
            err.code = "CPF_DUPLICADO";
            err.cpfDuplicado = {
              cpf: cpfFormatado,
              cpfDigits: cpfDigitsTx,
              praticanteId: existing.praticanteId || null,
            };
            throw err;
          }
        }

        // 2. Garantir que tenha matrícula
        if (!praticanteData.matriculaCBTKD) {
          praticanteData.matriculaCBTKD = await this.newID();
          console.log("[PraticantesService] Matrícula gerada:", praticanteData.matriculaCBTKD);
        }
        
        // 3. Preparar dados com trigramas
        const nomeCompleto = praticanteData.dadosPessoais.name || "Nome não disponível";
        const trigamValue = generateTrigam(nomeCompleto);
        const normalizedTrigamValue = Array.from(generateNormalizedTrigam(nomeCompleto));
        const searchWords = this.generateSearchWords(nomeCompleto);
        
        const dadosCompletos = {
          ...praticanteData,
          dadosPessoais: {
            ...praticanteData.dadosPessoais,
            cpf: cpfFormatado,
            name: nomeCompleto.toUpperCase(),
            searchableName: normalizarSearchableName(nomeCompleto),
            trigam: trigamValue,
            normalizedtrigam: normalizedTrigamValue,
            searchWords: searchWords
          }
        };
        
        // 4. Criar documento com ID automático
        const docRef = doc(collection(db, "praticantes"));
        transaction.set(docRef, dadosCompletos);

        createdId = docRef.id;

        // 🛡️ ÍNDICE ÚNICO DE CPF: cria atomicamente junto ao praticante
        if (cpfIndexRef) {
          transaction.set(cpfIndexRef, {
            praticanteId: createdId,
            cpfFormatado: formatCpf(cpfDigitsTx),
            ownerid: praticanteData.metadados?.ownerid || null,
            createdAt: Timestamp.fromDate(new Date()),
          });
        }

        console.log("[PraticantesService] Cadastro simplificado criado na transaction:", createdId);
      });
      
      // 5. Operações APÓS a transação (não bloqueantes)
      console.log("[PraticantesService] Transaction concluída, executando pós-processamento");
      console.log(`[PraticantesService] ✅ ID do Praticante criado: ${createdId}`);
      
      // Criar usuário de autenticação e documento users (se tiver email)
      const email = praticanteData.dadosContato?.email;
      if (opcoes.criarLogin === false) {
        console.log("[PraticantesService] ℹ️ Login criado depois, pela importação");
      } else if (email && email.trim() !== '') {
        try {
          if (!isImportacao) console.log(`[PraticantesService] 🔐 Criando Auth User para: ${email}`);
          console.log(`[PraticantesService] 🎯 UID que será usado no Firebase Auth: ${createdId}`);
          
          // 🎯 CRÍTICO: Criar usuário no Firebase Authentication COM O MESMO ID
          {
            // 🎯 CRÍTICO: Criar documento em users collection COM O MESMO ID
            let newUser = UserModelo({ 
              uid: createdId, // ← UID do documento users = ID do praticante
              displayName: praticanteData.dadosPessoais.name 
            });
            
            const userSearchWords = this.generateSearchWords(praticanteData.dadosPessoais.name);
            
            newUser = {
              ...newUser,
              dadosPessoais: {
                ...newUser.dadosPessoais,
                name: praticanteData.dadosPessoais.name,
                searchableName: normalizarSearchableName(praticanteData.dadosPessoais.name),
                trigam: generateTrigam(praticanteData.dadosPessoais.name),
                searchWords: userSearchWords
              },
              dadosContato: {
                ...newUser.dadosContato,
                email: email,
                celular: praticanteData.dadosContato?.celular || '' // ✅ Sincronizar celular
              }
            };
            
            // P13: `users` não é mais gravado do cliente. A callable
            // `criarCadastroUsuario` cria a conta no Auth E o documento, numa
            // transação, com `metadados` ditado pelo servidor — inclusive
            // `successorId: ""`, cuja ausência tornava o cadastro invisível nas
            // listagens. Ver functions/cadastroUsuario.js.
            await criarCadastroUsuario({ users: newUser, uid: createdId, criarPraticante: false });
            console.log(`[PraticantesService] ✅ Documento users criado com ID: ${createdId}`);
            // Em importação, sem e-mail nem celular no log: seriam milhares de
            // contatos no console da aba (invariante §6.4 do plano).
            if (isImportacao) console.log("[PraticantesService] 📞 Dados de contato sincronizados");
            else console.log(`[PraticantesService] 📞 Dados de contato sincronizados: email=${email}, celular=${praticanteData.dadosContato?.celular || 'N/A'}`);
            // Auth User UID, users Doc ID e Praticante ID são o mesmo `createdId`
            // por construção: agora quem cria a conta é a callable, com o uid que
            // recebe. A divergência que estes logs vigiavam deixou de ser possível.
            console.log(`[PraticantesService] 🎉 Sincronização completa em ${createdId}`);
          }
        } catch (authError) {
          console.error("[PraticantesService] ❌ Erro ao criar usuário de autenticação (não crítico):", authError);
          console.error(`[PraticantesService]    Praticante ${createdId} foi criado, mas Auth/users falharam`);
          // Não falha a operação se houver erro na criação do usuário
          // O praticante já foi criado com sucesso
        }
      } else {
        console.log("[PraticantesService] ℹ️ Sem email - pulando criação de Auth User e users");
        console.log(`[PraticantesService] ✅ Praticante criado apenas: ${createdId}`);
      }
      
      // Atualizar view
      try {
        await this.updateView(createdId);
        console.log("[PraticantesService] View atualizada");
      } catch (viewError) {
        console.error("[PraticantesService] Erro ao atualizar view (não crítico):", viewError);
      }
      
      console.log("[PraticantesService] createSimplifiedTransaction concluído com sucesso");
      return { id: createdId };
      
    } catch (error) {
      console.error("[PraticantesService] Erro em createSimplifiedTransaction:", error);
      throw error;
    }
    });

  /**
   * 🎯 REGRA CRÍTICA: Sincronização de IDs
   * =====================================
   * Firebase Auth UID = users Doc ID = praticantes Doc ID
   * 
   * Este método cria um praticante com ID pré-definido e, opcionalmente,
   * cria também o Auth User e o documento users COM O MESMO ID.
   * 
   * @param {string} UID - ID único para usar em todos os lugares
   * @param {Object} value - Dados completos do praticante
   * @param {boolean} doNotCreateAuthenticationUser - Se true, não cria Auth User
   * @returns {Promise} - Promise do setDoc
   */
  createWithID = async (UID, value, doNotCreateAuthenticationUser) => {
    try {
      console.log(`[PraticantesService] createWithID iniciando com UID: ${UID}`);

      // 🛡️ VERIFICAÇÃO DE CPF DUPLICADO: Bloqueia criação se CPF já existe entre praticantes ativos
      const cpfValue = value.dadosPessoais?.cpf;
      if (cpfValue && cpfValue.replace(/\D/g, "").length === 11) {
        const cpfFormatado = cpfValue.replace(/\D/g, "").replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
        const cpfDuplicado = await this.isCPFRegistered(cpfFormatado, UID || "");
        if (cpfDuplicado) {
          throw new Error("CPF já cadastrado no sistema. Use o cadastro existente ou entre em contato com o suporte.");
        }
      }

      if (doNotCreateAuthenticationUser === undefined || doNotCreateAuthenticationUser === false) {
        console.log(`[PraticantesService] 🔐 Criando Auth User e users document`);
        console.log(`[PraticantesService] 🎯 UID que será usado: ${UID}`);
        
        // 🎯 CRÍTICO: Criar documento users COM O MESMO ID
        let newUser = UserModelo({ uid: value.metadados.ownerid, displayName: value.metadados.owner });
        const userSearchWords = this.generateSearchWords(value.dadosPessoais.name);
        newUser = {
          ...newUser,
          dadosPessoais: {
            ...newUser.dadosPessoais,
            name: value.dadosPessoais.name,
            searchableName: normalizarSearchableName(value.dadosPessoais.name),
            trigam: generateTrigam(value.dadosPessoais.name),
            searchWords: userSearchWords
          },
          dadosContato: {
            ...newUser.dadosContato,
            email: value.dadosContato?.email || '',
            celular: value.dadosContato?.celular || '' // ✅ Sincronizar celular
          }
        };
        
        // P13: a conta no Auth e o documento `users` são criados pela callable,
        // numa transação, com `metadados` ditado pelo servidor. Auth UID, users
        // docId e praticante docId são o mesmo `UID` por construção — a
        // divergência que os logs antigos vigiavam deixou de ser possível.
        const { uid: uidCriado } = await criarCadastroUsuario({
          users: newUser,
          uid: UID ? UID.toString() : undefined,
          criarPraticante: false,
        });
        UID = uidCriado;
        console.log(`[PraticantesService] ✅ Cadastro criado no servidor: ${UID}`);
      }

      // Geração de trigramas e searchWords para busca híbrida
      const nomeCompleto = value.dadosPessoais.name || "Nome não disponível";
      const trigamValue = generateTrigam(nomeCompleto);
      const normalizedTrigamValue = Array.from(generateNormalizedTrigam(nomeCompleto));
      const searchWords = this.generateSearchWords(nomeCompleto);
      
      value = { 
        ...value, 
        matriculaCBTKD: await this.newID(), 
        dadosPessoais: { 
          ...value.dadosPessoais, 
          name: nomeCompleto.toUpperCase(),
          searchableName: normalizarSearchableName(nomeCompleto),
          trigam: trigamValue,
          normalizedtrigam: normalizedTrigamValue,
          searchWords: searchWords // NOVO: Campo para busca híbrida
        } 
      };
      if (isNaN(value.metadados.creation)) value.metadados.creation = Timestamp.fromDate(new Date());
      if (value.metadados.owner === null) value.metadados.owner = value.dadosPessoais.name;

      // 🛡️ SANITIZAÇÃO: Garante que campos boolean nunca sejam undefined
      value.metadados = this.sanitizeMetadadosBooleanFields(value.metadados);

      // 🛡️ ÍNDICE ÚNICO DE CPF + praticante em transação atômica
      const cpfDigitsFinal = getCpfDigits(value.dadosPessoais?.cpf);
      await runTransaction(db, async (transaction) => {
        let cpfIndexRef = null;
        if (cpfDigitsFinal) {
          cpfIndexRef = doc(db, CPF_INDEX_COLLECTION, cpfDigitsFinal);
          const cpfIndexSnap = await transaction.get(cpfIndexRef);
          if (cpfIndexSnap.exists()) {
            const existing = cpfIndexSnap.data();
            if (existing.praticanteId && existing.praticanteId !== UID.toString()) {
              throw new Error(
                "CPF já cadastrado no sistema. Use o cadastro existente ou entre em contato com o suporte."
              );
            }
          }
        }

        transaction.set(doc(db, "praticantes", UID.toString()), value);

        if (cpfIndexRef) {
          transaction.set(cpfIndexRef, {
            praticanteId: UID.toString(),
            cpfFormatado: formatCpf(cpfDigitsFinal),
            ownerid: value.metadados?.ownerid || null,
            createdAt: Timestamp.fromDate(new Date()),
          });
        }
      });

      this.updateView(UID);
      return;
    } catch (error) {
      throw error;
    }
  };

  verifyEmail = async (email) => {
    try {
      let urlService = import.meta.env.VITE_BACKEND_API_URL + "/api/usr/chkemail";
      await axios.post(urlService, { email: email }).then((response) => {
        //console.log(response);
        return response.data.value;
      }, (error) => {
        //console.log(error);
        return false;
      });
    } catch (error) {
      throw error;
    }
  };

  updateAndPortalID = async (id, value, PortalID) => {
    // console.log("updateAndPortalID => id=", id, "value=", value, "PortalID=", PortalID);
    
    // 🔧 CORRIGIR: Preservar Timestamp de birthdate ao fazer spread operator
    const birthdate = value.dadosPessoais?.birthdate;
    const isTimestampBirthdate = birthdate && typeof birthdate.toDate === 'function';
    
    // Geração de trigramas e searchWords para busca híbrida
    const nomeCompleto = value.dadosPessoais.name || "Nome não disponível";
    const trigamValue = generateTrigam(nomeCompleto);
    const normalizedTrigamValue = Array.from(generateNormalizedTrigam(nomeCompleto));
    const searchWords = this.generateSearchWords(nomeCompleto);
    
    value = { 
      ...value, 
      dadosPessoais: { 
        ...value.dadosPessoais, 
        name: nomeCompleto.toUpperCase(),
        searchableName: normalizarSearchableName(nomeCompleto),
        trigam: trigamValue,
        normalizedtrigam: normalizedTrigamValue,
        searchWords: searchWords, // NOVO: Campo para busca híbrida
        // ✅ CRÍTICO: Garantir que birthdate permaneça como Timestamp
        birthdate: isTimestampBirthdate ? birthdate : value.dadosPessoais.birthdate
      } 
    };
    try {
      if (value.matriculaCBTKD === "") {
        value = { ...value, matriculaCBTKD: await this.newID() };
      }
      return await GenericService.getOne("praticantes", id).then(async (retData) => {
        // 🛡️ ÍNDICE ÚNICO DE CPF: se o CPF mudou, migra o índice ANTES do update
        // (o ID do praticante é estável; apenas o CPF pode mudar em edição)
        const cpfAntesDigits = getCpfDigits(retData?.dadosPessoais?.cpf);
        const cpfDepoisDigits = getCpfDigits(value?.dadosPessoais?.cpf);
        if (cpfAntesDigits !== cpfDepoisDigits) {
          try {
            await CPFIndexService.migrate(
              retData?.dadosPessoais?.cpf,
              value?.dadosPessoais?.cpf,
              id,
              value?.metadados?.ownerid || retData?.metadados?.ownerid || null
            );
          } catch (indexErr) {
            console.error("[PraticantesService.updateAndPortalID] Falha ao migrar índice de CPF:", indexErr.message);
            throw indexErr; // Propaga para bloquear update com CPF duplicado
          }
        } else if (cpfDepoisDigits) {
          // CPF não mudou — garante presença do índice (idempotente)
          try {
            await CPFIndexService.reserve(
              value.dadosPessoais.cpf,
              id,
              value?.metadados?.ownerid || retData?.metadados?.ownerid || null
            );
          } catch (reserveErr) {
            // Se já reservado pelo mesmo praticante, reserve é idempotente.
            // Caso bata em outro praticante, bloqueia.
            console.error("[PraticantesService.updateAndPortalID] Conflito ao reafirmar índice de CPF:", reserveErr.message);
            throw reserveErr;
          }
        }

        let oldVersion = {
          ...retData,
          metadados: {
            ...retData.metadados,
            active: false,
            successorId: id,
          },
        };
        return await addDoc(collection(db, "praticantes"), oldVersion).then(async (lastVersion) => {
          // ✅ CRÍTICO: Preservar birthdate com spread operator
          let ActualVersion = {
            ...value,
            dadosPessoais: {
              ...value.dadosPessoais,
              // ✅ Reafirmar que birthdate é Timestamp (não Map)
              birthdate: isTimestampBirthdate ? birthdate : value.dadosPessoais.birthdate
            },
            metadados: {
              ...value.metadados,
              version: value.metadados.version + 1,
              successorId: "",
            },
          };
          return await updateDoc(doc(db, "praticantes", id), ActualVersion).then(async () => {
            this.updateView(id);
            if (ActualVersion.dadosColaborador !== oldVersion.dadosColaborador) {
              // 🛡️ Helper para normalizar administrativo (aceita objeto legado ou array)
              const getNormalized = (dadosColaborador) => {
                const adm = dadosColaborador?.administrativo;
                if (!adm) return null;
                if (Array.isArray(adm)) return adm.length > 0 ? adm[0] : null;
                return adm; // objeto legado
              };

              const oldAdm = getNormalized(oldVersion.dadosColaborador);
              const newAdm = getNormalized(value.dadosColaborador);

              if (oldAdm && !newAdm) {
                // Tinha administrativo, agora não tem → suprimir regras.
                // Pela Cloud Function desde 09/09/2026 (P18): a regra de `users`
                // exige 100/102 para escrever `usersgroupsowned` e quem edita
                // pessoa tem 300/302. A function subtrai da remoção os grupos
                // que outros vínculos do mesmo portal ainda sustentam.
                return await gruposDeVinculo({
                  uid: id,
                  portalID: oldAdm.idEmpregador || PortalID,
                  origem: "cargo",
                  origemId: oldAdm.uidDepartamento,
                  cargoId: oldAdm.uidCargoColaborador,
                  idEmpregador: oldAdm.idEmpregador,
                  remover: true,
                }).then(async () => {
                  return await this.updateEntiteWithColaboradorDepartamentoCargo(
                    id,
                    value,
                    oldAdm.idEmpregador
                  );
                });
              } else if (oldAdm && newAdm) {
                if (oldAdm.uidDepartamento && oldAdm.uidCargoColaborador) {
                  // Tinha cargo anterior → troca de cargo: remove o antigo e
                  // concede o novo, os dois pela Cloud Function (P18). Em
                  // sequência, e não em paralelo: se o cargo novo repete um grupo
                  // do antigo, conceder depois de remover é o que deixa o grupo
                  // de pé no fim.
                  return await gruposDeVinculo({
                    uid: id,
                    portalID: oldAdm.idEmpregador || PortalID,
                    origem: "cargo",
                    origemId: oldAdm.uidDepartamento,
                    cargoId: oldAdm.uidCargoColaborador,
                    idEmpregador: oldAdm.idEmpregador,
                    remover: true,
                  }).then(async () => await gruposDeVinculo({
                    uid: id,
                    portalID: newAdm.idEmpregador || oldAdm.idEmpregador || PortalID,
                    origem: "cargo",
                    origemId: newAdm.uidDepartamento,
                    cargoId: newAdm.uidCargoColaborador,
                  })).then(async () => {
                    return await this.updateEntiteWithColaboradorDepartamentoCargo(
                      id,
                      value,
                      newAdm.idEmpregador
                    );
                  });
                } else {
                  // Não tinha cargo antes → adicionar (Cloud Function, P18)
                  return await gruposDeVinculo({
                    uid: id,
                    portalID: newAdm.idEmpregador || PortalID,
                    origem: "cargo",
                    origemId: newAdm.uidDepartamento,
                    cargoId: newAdm.uidCargoColaborador,
                  }).then(async () => {
                    return await this.updateEntiteWithColaboradorDepartamentoCargo(
                      id,
                      value,
                      newAdm.idEmpregador
                    );
                  });
                }
              } else if (newAdm) {
                // Não tinha administrativo, agora tem → adicionar (Cloud Function, P18)
                return await gruposDeVinculo({
                  uid: id,
                  portalID: newAdm.idEmpregador || PortalID,
                  origem: "cargo",
                  origemId: newAdm.uidDepartamento,
                  cargoId: newAdm.uidCargoColaborador,
                }).then(async () => {
                  return await this.updateEntiteWithColaboradorDepartamentoCargo(
                    id,
                    value,
                    newAdm.idEmpregador
                  );
                });
              } else {
                return doc;
              }
            }
          });
        });
      });
    } catch (error) {
      throw error;
    }
  };

  update = async (id, value) => {
    // 🔧 CORRIGIR: Preservar Timestamp de birthdate ao fazer spread operator
    // Quando o spread operator é usado, Timestamps são convertidos em {seconds, nanoseconds}
    // Precisamos explicitamente preservar os Timestamps antes de fazer a cópia
    const birthdate = value.dadosPessoais?.birthdate;
    const isTimestampBirthdate = birthdate && typeof birthdate.toDate === 'function';
    
    // Geração de trigramas e searchWords para busca híbrida
    const nomeCompleto = value.dadosPessoais.name || "Nome não disponível";
    const trigamValue = generateTrigam(nomeCompleto);
    const normalizedTrigamValue = Array.from(generateNormalizedTrigam(nomeCompleto));
    const searchWords = this.generateSearchWords(nomeCompleto);
    
    value = { 
      ...value, 
      dadosPessoais: { 
        ...value.dadosPessoais, 
        name: nomeCompleto.toUpperCase(),
        searchableName: normalizarSearchableName(nomeCompleto),
        trigam: trigamValue,
        normalizedtrigam: normalizedTrigamValue,
        searchWords: searchWords, // NOVO: Campo para busca híbrida
        // ✅ CRÍTICO: Garantir que birthdate permaneça como Timestamp (não como Map)
        birthdate: isTimestampBirthdate ? birthdate : value.dadosPessoais.birthdate
      } 
    };
    //console.log("id=", id, "value=", value);
    try {
      if (value.matriculaCBTKD === "") {
        value = { ...value, matriculaCBTKD: await this.newID() };
      }
      // 🔧 CORREÇÃO CRÍTICA: retornar a cadeia de promises para que erros do Firestore
      // sejam propagados ao chamador. Sem o "return" aqui, o await em doSaveAllData
      // resolve imediatamente (undefined) e qualquer falha de escrita é descartada
      // silenciosamente, exibindo toast de sucesso mesmo quando o dado NÃO foi salvo.
      return GenericService.getOne("praticantes", id).then((retData) => {
        //console.log("retData=", retData);
        let oldVersion = {
          ...retData,
          metadados: {
            ...retData.metadados,
            active: false,
            successorId: id,
          },
        };
        return addDoc(collection(db, "praticantes"), oldVersion).then((lastVersion) => {
          // ✅ CRÍTICO: Preservar birthdate com spread operator
          // Usando Timestamp explicitamente para garantir que NÃO vire Map
          let ActualVersion = {
            ...value,
            dadosPessoais: {
              ...value.dadosPessoais,
              // ✅ Reafirmar que birthdate é Timestamp (não Map com seconds/nanoseconds)
              birthdate: isTimestampBirthdate ? birthdate : value.dadosPessoais.birthdate
            },
            metadados: {
              ...value.metadados,
              version: value.metadados.version + 1,
              active: retData.metadados.active,
              successorId: "",
            },
          };

          // 🛡️ SANITIZAÇÃO: Garante que campos boolean nunca sejam undefined
          ActualVersion.metadados = this.sanitizeMetadadosBooleanFields(ActualVersion.metadados);

          // 🛡️ SEGURANÇA CRÍTICA: Prevenir perda de historicoGraduacoes
          // Se o update está enviando historicoGraduacoes vazio (tudo strings/objetos sem data)
          // mas o Firestore tem dados reais, preserva os dados existentes.
          // Isso é uma rede de segurança para o caso de o componente não ter carregado as graduações.
          if (ActualVersion.dadosTaekwondo && retData.dadosTaekwondo?.historicoGraduacoes) {
            const temDadosReais = (hist) =>
              Array.isArray(hist) && hist.some(h => h && typeof h === 'object' && h.data != null && h.data !== "");

            const historicoAtual = ActualVersion.dadosTaekwondo.historicoGraduacoes;
            const historicoFirestore = retData.dadosTaekwondo.historicoGraduacoes;

            if (!temDadosReais(historicoAtual) && temDadosReais(historicoFirestore)) {
              console.warn("⚠️ [UPDATE] Perda de historicoGraduacoes detectada! Preservando dados existentes do Firestore.");
              ActualVersion = {
                ...ActualVersion,
                dadosTaekwondo: {
                  ...ActualVersion.dadosTaekwondo,
                  historicoGraduacoes: historicoFirestore,
                  HistoricoGraduacoes: retData.dadosTaekwondo.HistoricoGraduacoes ?? ActualVersion.dadosTaekwondo.HistoricoGraduacoes,
                  maxGraduacao: retData.dadosTaekwondo.maxGraduacao ?? ActualVersion.dadosTaekwondo.maxGraduacao,
                  MaxGraduacao: retData.dadosTaekwondo.MaxGraduacao ?? ActualVersion.dadosTaekwondo.MaxGraduacao,
                }
              };
            }
          }

          //console.log("update => id=", id, "ActualVersion=", ActualVersion);
          return updateDoc(doc(db, "praticantes", id), ActualVersion).then(async (retValue) => {
            if (oldVersion.dadosContato.email !== "" && oldVersion.dadosContato.email !== ActualVersion.dadosContato.email) {
              UsersService.updateEmail(id, ActualVersion.dadosContato.email).catch((error) => {
                console.log(error);
                throw error;
              });
            }

            if (oldVersion.dadosPessoais.name !== "" && oldVersion.dadosPessoais.name !== ActualVersion.dadosPessoais.name) {
              UsersService.updateName(id, ActualVersion.dadosPessoais.name);
            }
            this.updateView(id);
            return retValue;
          });
        });
      });
    } catch (error) {
      throw error;
    }
  };

  updateActiveState = async (id, newStateValue) => {
    try {
      const retData = await GenericService.getOne("praticantes", id);
      if (!retData) return;

      // 1. Cria backup da versão atual (antes da mudança)
      const backup = {
        ...retData,
        metadados: {
          ...retData.metadados,
          active: false,       // Versões históricas são sempre inativas
          successorId: id,     // Aponta para o documento original (atual)
        },
      };
      await addDoc(collection(db, "praticantes"), backup);

      // 2. Atualiza o original num único write, sem estado intermediário
      let updateFields = {
        "metadados.active": newStateValue,
        "metadados.successorId": "",
        "metadados.version": (retData.metadados?.version || 0) + 1,
      };
      if (retData.matriculaCBTKD === "") {
        const novoID = await this.newID();
        updateFields["matriculaCBTKD"] = novoID;
      }
      await updateDoc(doc(db, "praticantes", id), updateFields, { merge: true });
      this.updateView(id);
    } catch (error) {
      throw error;
    }
  };

  /**
   * Anexa um documento à galeria do praticante.
   *
   * ⚠️ NÃO voltar para o padrão "ler tudo, concatenar, gravar o array inteiro".
   * A versão anterior fazia exatamente isso, e ainda por cima sem `await`: a
   * promessa da leitura ficava solta dentro de um try/catch, de modo que
   * `await includeDocument(...)` retornava ANTES de a escrita acontecer e
   * qualquer erro morria no console. Dois anexos seguidos (o caso normal de um
   * para-atleta que precisa mandar mais de um arquivo de laudo) liam o mesmo
   * array e o segundo apagava o primeiro.
   *
   * `arrayUnion` resolve o acréscimo no servidor, sem ler antes, e a promessa
   * agora é devolvida de verdade — quem chama consegue esperar e consegue
   * tratar a falha.
   */
  includeDocument = async (id, documentValue) => {
    return this.includeDocuments(id, [documentValue]);
  };

  /**
   * Mesma coisa que includeDocument, para vários documentos de uma vez —
   * uma única escrita, sem janela para perder anexo pelo caminho.
   */
  includeDocuments = async (id, documentValues) => {
    const lista = (documentValues || []).filter(Boolean);
    if (!id || lista.length === 0) return;

    await updateDoc(doc(db, "praticantes", id), { documentos: arrayUnion(...lista) });

    // A view é um espelho de leitura: se ela falhar, o documento já está no
    // cadastro e não se deve desfazer o anexo por causa disso.
    //
    // ⚠️ O `catch` abaixo pega MENOS do que parece. `updateView` é `async` mas
    // não devolve a promessa da escrita interna (`GenericService.getOne(...).then(...)`
    // fica solta), então este `await` volta na hora e uma falha lá dentro vira
    // rejeição não tratada, não este `console.warn`. O catch cobre só um erro
    // SÍNCRONO da chamada. Corrigir `updateView` mudaria o comportamento de
    // dezenas de chamadores e não cabe nesta branch — fica o registro para não
    // se confiar numa proteção que não existe.
    try {
      await this.updateView(id);
    } catch (error) {
      console.warn("includeDocuments => falha ao atualizar a view de", id, error);
    }
  };

  removeDocument = async (id, href) => {
    // //console.log("removeDocument => id = ", id, " href = ", href);
    try {
      GenericService.getOne("praticantes", id).then(async (retData) => {
        let propertyName = "documentos";
        let newDocumentList = retData.documentos.filter((doc) => doc.href !== href);
        updateDoc(doc(db, "praticantes", id), { [propertyName]: newDocumentList }, { merge: true })
          .then((retValue) => {
            // //console.log("retValue=", retValue);
            this.updateView(id);
            return retValue;
          }).catch((error) => {
            console.log(error);
            throw error;
          });
      }).catch((error) => {
        console.log(error);
        throw error;
      });
    } catch (error) {
      console.log(error);
      throw error;
    }
  };



  updatePropertyOnVinculo = async (id, propertyName, propertyValue) => {
    // 🛡️ SANITIZAÇÃO: Se estiver atualizando metadados inteiros, sanitiza
    let valueToUpdate = propertyValue;
    if (propertyName === 'metadados' && typeof propertyValue === 'object') {
      valueToUpdate = this.sanitizeMetadadosBooleanFields(propertyValue);
    }

    updateDoc(doc(db, "praticantes", id), { [propertyName]: valueToUpdate }, { merge: true })
      .then((retValue) => {
        // //console.log("retValue=", retValue);
        this.updateView(id);
        return retValue;
      }).catch((error) => {
        throw error;
      });
  };

  /**
   * Atualiza múltiplas propriedades de um registro de praticante
   * @param {string} id - ID do documento a ser atualizado
   * @param {Object} properties - Objeto contendo as propriedades e valores a serem atualizados
   * @returns {Promise} - Promise que resolve quando a atualização é concluída
   * 
   * Exemplos de uso:
   * 
   * // Atualizar aprovações do mestre e federação
   * await updatePropertiesOnRegister("praticanteId", {
   *   "metadados.aprovadoMestre": true,
   *   "metadados.aprovadoFederacao": false
   * });
   * 
   * // Atualizar dados pessoais múltiplos
   * await updatePropertiesOnRegister("praticanteId", {
   *   "dadosPessoais.apelido": "Novo apelido",
   *   "dadosPessoais.nacionalidade": "Brasil",
   *   "dadosContato.telefone": "(11) 99999-9999"
   * });
   * 
   * // Atualizar estado ativo e data de modificação
   * await updatePropertiesOnRegister("praticanteId", {
   *   "metadados.active": true,
   *   "metadados.date": Timestamp.fromDate(new Date()),
   *   "metadados.version": 2
   * });
   */
  updatePropertiesOnRegister = async (id, properties) => {
    try {
      // Valida os parâmetros de entrada
      if (!id || typeof id !== 'string') {
        throw new Error('ID do documento é obrigatório e deve ser uma string');
      }

      if (!properties || typeof properties !== 'object' || Object.keys(properties).length === 0) {
        throw new Error('Propriedades são obrigatórias e devem ser um objeto não vazio');
      }

      // Verifica se o nome está sendo atualizado e gera searchWords
      let finalProperties = { ...properties };
      
      if (properties['dadosPessoais.name'] || (properties.dadosPessoais && properties.dadosPessoais.name)) {
        const nomeCompleto = properties['dadosPessoais.name'] || properties.dadosPessoais.name;
        const searchWords = this.generateSearchWords(nomeCompleto);
        
        // Adiciona searchWords às propriedades a serem atualizadas
        if (properties['dadosPessoais.name']) {
          finalProperties['dadosPessoais.searchWords'] = searchWords;
        } else if (properties.dadosPessoais) {
          finalProperties.dadosPessoais = {
            ...finalProperties.dadosPessoais,
            searchWords: searchWords
          };
        }
      }

      // Atualiza o documento com merge para preservar dados existentes
      await updateDoc(doc(db, "praticantes", id), finalProperties, { merge: true });
      
      // Atualiza a view após a modificação
      this.updateView(id);
      
      return { success: true, id, updatedProperties: Object.keys(finalProperties) };

    } catch (error) {
      console.error(`updatePropertiesOnRegister => Erro ao atualizar documento ${id}:`, error);
      throw error;
    }
  };

  /*
   * MÉTODOS AUXILIARES PARA APROVAÇÕES - Podem ser usados em componentes
   * 
   * // Aprovar cadastro pelo mestre
   * async aprovarCadastroPorMestre(praticanteId, aprovado = true) {
   *   return this.updatePropertiesOnRegister(praticanteId, {
   *     "metadados.aprovadoMestre": aprovado,
   *     "metadados.dataAprovacaoMestre": aprovado ? Timestamp.fromDate(new Date()) : null
   *   });
   * }
   * 
   * // Aprovar cadastro pela federação
   * async aprovarCadastroPorFederacao(praticanteId, aprovado = true) {
   *   return this.updatePropertiesOnRegister(praticanteId, {
   *     "metadados.aprovadoFederacao": aprovado,
   *     "metadados.dataAprovacaoFederacao": aprovado ? Timestamp.fromDate(new Date()) : null
   *   });
   * }
   * 
   * // Atualizar estado de ativação
   * async atualizarEstadoAtivacao(praticanteId, ativo = true) {
   *   return this.updatePropertiesOnRegister(praticanteId, {
   *     "metadados.active": ativo,
   *     "metadados.dataUltimaModificacao": Timestamp.fromDate(new Date())
   *   });
   * }
   */


  delete = async (id) => {
    try {
      GenericService.getOne("praticantes", id).then((retData) => {
        let ActualVersion = {
          ...retData,
          metadados: {
            ...retData.metadados,
            active: false,
          },
        };
        updateDoc(doc(db, "praticantes", id), ActualVersion).then((retValue) => {
          this.updateView(id);
          return retValue;
        });
      });
    } catch (error) {
      throw error;
    }
  };

  async updateEntiteWithColaboradorDepartamentoCargo(id, value, idEmpregador) {
    if (!idEmpregador) return;
    try {
      const entidadeSnapshot = await getDoc(doc(db, "entidades", idEmpregador));
      if (!entidadeSnapshot.exists()) return;

      let entidade = entidadeSnapshot.data();
      if (!Array.isArray(entidade.colaboradores)) return;

      // Normalizar administrativo (aceita objeto legado ou array)
      const adm = value.dadosColaborador?.administrativo;
      let admObj = null;
      if (Array.isArray(adm) && adm.length > 0) {
        // Buscar o vínculo específico desta entidade
        admObj = adm.find(v => v.idEmpregador === idEmpregador) || adm[0];
      } else if (adm && typeof adm === 'object') {
        admObj = adm;
      }

      let updated = false;
      for (const colaborador of entidade.colaboradores) {
        if (colaborador.uid === id) {
          if (!admObj) return;
          colaborador.DepartamentoCargo = (admObj.departamento || "") + " / " + (admObj.cargoColaborador || "");
          updated = true;
          break;
        }
      }

      if (updated) {
        await updateDoc(doc(db, "entidades", idEmpregador), entidade);
        this.updateView(id);
      }
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  /**
   * ⚠️ DESATIVADOS em 09/09/2026 (P18) — os três métodos abaixo.
   *
   * Gravavam `users.usersgroupsowned` direto do client. Desde 04/09/2026 a regra
   * de `users` exige 100/102 (Grupos de usuários) para tocar nesse campo, e quem
   * administra colaborador tem 300/302: pela fonte, cortando versões arquivadas
   * por `metadados.successorId`, são 104 pessoas com 300/302 contra 3 com
   * 100/102. Toda chamada destes métodos passou a ser NEGADA.
   *
   * A falha não era só de funcionalidade. Em `ColaboradoresComponent`, o `await`
   * estourava antes do passo que exclui o vínculo em
   * `praticantes.dadosColaborador.administrativo` — o operador via "Erro ao
   * excluir membro", o vínculo continuava lá e o colaborador removido continuava
   * COM OS GRUPOS DO CARGO. Revogação que falha é pior que funcionalidade que
   * falha.
   *
   * ── A anotação anterior estava errada ────────────────────────────────────
   * `updateUserAddRules...` e `updateUserAdjustRules...` foram marcados em 04/09
   * como "DESCONTINUADO — sem chamadores". Tinham quatro, todos DENTRO deste
   * mesmo arquivo: o grep que gerou a nota excluía o próprio
   * `Praticantes.Service.js`. Por causa dela o P18 foi registrado como caso
   * isolado de um chamador, quando eram sete em três métodos, quebrando remover
   * colaborador, adicionar cargo, TROCAR cargo e a mudança de status
   * administrativo.
   *
   * ── O que substituiu ─────────────────────────────────────────────────────
   * A Cloud Function `gruposDeVinculo` (functions/grupoPadraoUsuario.js), que
   * roda com Admin SDK, valida 300/302 no servidor, lê os grupos do PRÓPRIO
   * cargo (o client não os escolhe) e — o que não dava para levar do client —
   * varre os OUTROS vínculos ativos da pessoa no mesmo portal e subtrai da
   * remoção os grupos que eles ainda sustentam. Essa conta vivia em
   * `_collectProtectedRulesForUser`, aqui, e foi para o servidor junto com a
   * escrita: quem decide o que pode sair tem de ser quem lê os vínculos.
   */
  async _collectProtectedRulesForUser() {
    throw new Error(
      "_collectProtectedRulesForUser foi descontinuado. A proteção multi-vínculo vive na Cloud Function gruposDeVinculo."
    );
  }

  async updateUserSupressRulesByIdDepatamentAndIdCargo() {
    throw new Error(
      "updateUserSupressRulesByIdDepatamentAndIdCargo foi descontinuado. Use gruposDeVinculo({ remover: true, vinculoId | idEmpregador })."
    );
  }

  async updateUserAddRulesByIdDepatamentAndIdCargo() {
    throw new Error(
      "updateUserAddRulesByIdDepatamentAndIdCargo foi descontinuado. Use gruposDeVinculo (Cloud Function)."
    );
  }

  async updateUserAdjustRulesByIdDepatamentAndIdCargo() {
    throw new Error(
      "updateUserAdjustRulesByIdDepatamentAndIdCargo foi descontinuado. Use gruposDeVinculo duas vezes: remover o cargo antigo e conceder o novo."
    );
  }

  async getPortalId(portalID) {
    if (portalID === undefined || portalID === "") {
      return portalID;
    } else {
      // return portalID;
      return await getDoc(doc(db, "entidades", portalID))
        .then(async (entidadedoc) => {
          if (entidadedoc.exists()) {
            // console.log("entidadedoc.data().dadosPessoais.tipoEntidade=", entidadedoc.data().dadosPessoais.tipoEntidade);
            // console.log("entidadedoc.data().dadosFiliacao=", entidadedoc.data().dadosFiliacao);
            // console.log("entidadedoc.data().dadosFiliacao.federacaoID=", entidadedoc.data().dadosFiliacao.federacaoID);
            if (entidadedoc.data().dadosPessoais.tipoEntidade === "Confederação") return portalID;
            else if (entidadedoc.data().dadosPessoais.tipoEntidade === "Federação") return portalID;
            else if (entidadedoc.data().dadosFiliacao.federacaoID === undefined)
              return await this.getPortalIdBySigla(entidadedoc.data().dadosFiliacao.federacao);
            return entidadedoc.data().dadosFiliacao.federacaoID;
          }
          else return portalID;
        }).catch((error) => {
          console.log(error);
          return portalID;
        });
    }
  }

  async getPortalIdBySigla(siglaEntidade) {
    // console.log("getPortalIdBySigla(siglaEntidade=", siglaEntidade, ")");
    if (siglaEntidade === undefined || siglaEntidade === "") {
      return undefined;
    }
    return await GenericVinculadoService.getCollectionByConditions("entidades", [
      where("dadosPessoais.sigla", "==", siglaEntidade),
      where("metadados.successorId", "==", ""),
      where("metadados.active", "==", true),
      orderBy("metadados.creation", "desc")])
      .then((querySnapshot) => {
        // console.log("querySnapshot=", querySnapshot);
        if (querySnapshot[0].length > 0) {
          // console.log("querySnapshot[0][0].id=", querySnapshot[0][0].id);
          return querySnapshot[0][0].id;
        }
        return undefined;
      }).catch((error) => {
        console.log(error);
        return undefined;
      });
  }


  updateView = async (id) => {
    // console.log("updateView => id=", id);
    try {
      GenericService.getOne("praticantes", id).then(async (value) => {
        // console.log("updateView => value=", value);

        // Geração de trigramas e searchWords para busca híbrida
        const nomeCompleto = value.dadosPessoais.name || "Nome não disponível";
        const trigamValue = generateTrigam(nomeCompleto);
        const normalizedTrigamValue = Array.from(generateNormalizedTrigam(nomeCompleto));
        const searchWords = this.generateSearchWords(nomeCompleto);

        // Calcula se o praticante deve ser exibido na listagem de filiados
        const metadados = value.metadados || {};
        const exibirEmFiliados = !(
          metadados.blocked === true ||
          metadados.bloqueio?.bloqueado === true ||
          metadados.bloqueioAdministrativo === true ||
          metadados.cancelado === true ||
          metadados.desfiliado === true
        );

        let newViewData = {
          id: id,
          imageURL: value.dadosPessoais.urlImagemID ? value.dadosPessoais.urlImagemID : "",
          dadosPessoais: {
            name: nomeCompleto.toUpperCase(),
            searchableName: normalizarSearchableName(nomeCompleto),
            trigam: trigamValue,
            normalizedtrigam: normalizedTrigamValue,
            searchWords: searchWords // NOVO: Campo para busca híbrida
          },
          MaxGraduacao: value.dadosTaekwondo.MaxGraduacao,
          uf: value.dadosEndereco.logradouroUF,
          birthdate: value.dadosPessoais.birthdate,
          sexo: value.dadosPessoais.sexo ? value.dadosPessoais.sexo : "Não informado",
          filiacao: value.dadosFiliacao.federacao,
          federacaoID: value.dadosFiliacao.federacaoID,
          vinculadoEntidadeID: value.dadosFiliacao.vinculadoEntidadeID ? value.dadosFiliacao.vinculadoEntidadeID : "",
          metadados: { 
            active: value.metadados.active, 
            federativeactive: value.metadados.federativeactive, 
            exibirEmFiliados: exibirEmFiliados,  // NOVO: Campo calculado para filtragem eficiente
            version: 1 
          },
        };

        // 🛡️ SANITIZAÇÃO: Garante que campos boolean nunca sejam undefined
        newViewData.metadados = this.sanitizeMetadadosBooleanFields(newViewData.metadados);

        setDoc(doc(db, "praticantes.view", id), newViewData)
          .then(() => {
            // console.log("updateView => setDoc praticantes.view => newViewData=", newViewData);
            // return newViewData;
          })
          .catch((error) => {
            throw error;
          });


        // GenericService.getOne("praticantes.view", id).then((retData) => {
        //   if (retData) {
        //     updateDoc(doc(db, "praticantes.view", id), newViewData);
        //   } else {
        //     setDoc(doc(db, "praticantes.view", id), newViewData);
        //   }
        // });
      }).catch((error) => {
        throw error;
      });
    } catch (error) {
      throw error;
    }
  };

  /*
   * EXEMPLO DE USO DO updatePropertiesOnRegister NO COMPONENTE:
   * 
   * import PraticanteService from "../../../services/Praticantes.Service";
   * 
   * // No componente React:
   * const handleAprovarMestre = async () => {
   *   try {
   *     await PraticanteService.updatePropertiesOnRegister(queryParams.get("id"), {
   *       "metadados.aprovadoMestre": true,
   *       "metadados.dataAprovacaoMestre": Timestamp.fromDate(new Date())
   *     });
   *     console.log("Cadastro aprovado pelo mestre com sucesso!");
   *   } catch (error) {
   *     console.error("Erro ao aprovar cadastro:", error);
   *   }
   * };
   * 
   * // Para atualizar múltiplos campos de uma vez:
   * const handleAtualizarMultiplosCampos = async () => {
   *   const updates = {
   *     "metadados.aprovadoMestre": data.metadados.aprovadoMestre,
   *     "metadados.aprovadoFederacao": data.metadados.aprovadoFederacao,
   *     "metadados.active": data.metadados.active,
   *     "metadados.dataUltimaModificacao": Timestamp.fromDate(new Date())
   *   };
   *   
   *   await PraticanteService.updatePropertiesOnRegister(queryParams.get("id"), updates);
   * };
   */

  /**
   * MÉTODO UTILITÁRIO: Gera trigramas normalizados localmente
   */
  generateNormalizedTrigramsLocal = (text) => {
    if (!text || typeof text !== 'string') return [];
    
    // Normalizar texto (remover acentos, converter para minúsculas)
    const normalizedText = text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
    
    const trigramas = new Set();
    
    // Gerar trigramas de 3 caracteres
    for (let i = 0; i <= normalizedText.length - 3; i++) {
      const trigrama = normalizedText.substring(i, i + 3);
      if (trigrama.length === 3 && trigrama.trim().length === 3) {
        trigramas.add(trigrama);
      }
    }
    
    return Array.from(trigramas);
  };

  /**
   * MÉTODO UTILITÁRIO: Delay assíncrono
   */
  delay = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };

  /**
   * Busca avançada por múltiplos trigramas normalizados
   * Divide o termo de busca em palavras e faz uma busca por trigrama para cada palavra,
   * retornando apenas os documentos que coincidem com TODAS as palavras (interseção)
   * 
   * @param {string} queryTarget - Campo alvo da busca (geralmente "dadosPessoais.normalizedtrigam")  
   * @param {string} searchTerm - Termo de busca com múltiplas palavras
   * @param {number} queryLimit - Limite de resultados por consulta individual
   * @param {boolean} onlyActive - Filtrar apenas registros ativos
   * @param {boolean} showAllVersions - Incluir versões anteriores
   * @param {string} dadosFiliacao_federacaoNome - Filtro por federação
   * @param {string} dadosFiliacao_vinculadoAEntidadeNome - Filtro por entidade
   * @param {Object} lastVisible - Documento para paginação
   * @returns {Promise<Array>} - Array de documentos que coincidem com todas as palavras
   */
  /**
   * Extrai palavras para busca híbrida otimizada
   */
  extractSearchWords(text) {
    if (!text) return [];
    
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^a-z0-9\s]/g, ' ') // Remove caracteres especiais
      .split(/\s+/)
      .filter(word => word.length >= 2) // Palavras com pelo menos 2 caracteres
      .filter((word, index, arr) => arr.indexOf(word) === index); // Remove duplicatas
  }

  /**
   * Remove letras dobradas de uma palavra
   * Exemplos: villela → vilela, ferrari → ferari, assunto → asunto
   */
  removeDoubleLetters(word) {
    return word.replace(/([a-z])\1+/g, '$1');
  }

  /**
   * Gera array de palavras completas para indexação
   */
  generateSearchWords(nome) {
    if (!nome) return [];
    
    const words = this.extractSearchWords(nome);
    const searchWords = [];
    
    // Adiciona palavras completas
    words.forEach(word => {
      if (word.length >= 2) {
        searchWords.push(word);
        
        // Adiciona variações sem letras dobradas
        const wordWithoutDoubles = this.removeDoubleLetters(word);
        if (wordWithoutDoubles !== word && !searchWords.includes(wordWithoutDoubles)) {
          searchWords.push(wordWithoutDoubles);
        }
      }
    });
    
    // Adiciona prefixos importantes (para nomes parciais)
    words.forEach(word => {
      if (word.length >= 4) {
        for (let i = 3; i <= Math.min(word.length - 1, 6); i++) {
          const prefix = word.substring(0, i);
          if (!searchWords.includes(prefix)) {
            searchWords.push(prefix);
            
            // Adiciona variação do prefixo sem letras dobradas
            const prefixWithoutDoubles = this.removeDoubleLetters(prefix);
            if (prefixWithoutDoubles !== prefix && !searchWords.includes(prefixWithoutDoubles)) {
              searchWords.push(prefixWithoutDoubles);
            }
          }
        }
      }
    });
    
    return searchWords.sort();
  }

  /**
   * BUSCA HÍBRIDA OTIMIZADA: Palavras Completas + Trigramas
   * Performance 10x melhor que busca só por trigramas
   */
  searchHybridOptimized = async (
    queryTarget,
    searchTerm,
    queryLimit = 200,
    onlyActive = true,
    showAllVersions = false,
    dadosFiliacao_federacaoNome = null,
    dadosFiliacao_vinculadoAEntidadeNome = null,
    lastVisible = null
  ) => {
    try {
      // console.log("🚀 BUSCA HÍBRIDA OTIMIZADA iniciada com:", {
      //   queryTarget,
      //   searchTerm,
      //   queryLimit,
      //   onlyActive,
      //   showAllVersions
      // });

      const words = this.extractSearchWords(searchTerm);

      if (words.length === 0) {
        return [];
      }

      // 🟢 FASE 1: Busca RÁPIDA por palavras completas
      const exactWordResults = await this.searchByExactWords(
        words,
        queryLimit,
        onlyActive,
        showAllVersions,
        dadosFiliacao_federacaoNome,
        dadosFiliacao_vinculadoAEntidadeNome
      );

      if (exactWordResults.length > 0) {
        return exactWordResults;
      }

      // 🟡 FASE 2: Fallback para trigramas apenas se necessário
      const trigramResults = await this.searchByMultipleNormalizedTrigrams(
        queryTarget,
        searchTerm,
        queryLimit,
        onlyActive,
        showAllVersions,
        dadosFiliacao_federacaoNome,
        dadosFiliacao_vinculadoAEntidadeNome,
        lastVisible
      );

      return trigramResults;

    } catch (error) {
      console.error("❌ Erro na busca híbrida otimizada:", error);
      throw error;
    }
  }

  /**
   * Busca por palavras completas (MUITO RÁPIDA)
   */
  searchByExactWords = async (
    words,
    queryLimit,
    onlyActive = true,
    showAllVersions = false,
    dadosFiliacao_federacaoNome = null,
    dadosFiliacao_vinculadoAEntidadeNome = null
  ) => {
    try {
      if (words.length === 1) {
        // Busca simples para uma palavra
        return await this.searchSingleExactWord(
          words[0],
          queryLimit,
          onlyActive,
          showAllVersions,
          dadosFiliacao_federacaoNome,
          dadosFiliacao_vinculadoAEntidadeNome
        );
      }

      // Busca com interseção para múltiplas palavras
      let results = [];

      for (let i = 0; i < words.length; i++) {
        const word = words[i];

        const wordResults = await this.searchSingleExactWord(
          word,
          queryLimit,
          onlyActive,
          showAllVersions,
          dadosFiliacao_federacaoNome,
          dadosFiliacao_vinculadoAEntidadeNome
        );

        if (i === 0) {
          // Primeira palavra: inicializa resultados
          results = wordResults;
        } else {
          // Demais palavras: faz interseção
          const currentIds = wordResults.map(doc => doc.id);
          results = results.filter(doc => currentIds.includes(doc.id));
        }

        // Otimização: para se não há interseção
        if (results.length === 0) {
          break;
        }
      }

      return results;

    } catch (error) {
      console.error("❌ Erro na busca por palavras completas:", error);
      return [];
    }
  }

  /**
   * Busca uma única palavra completa
   */
  searchSingleExactWord = async (
    word,
    queryLimit,
    onlyActive = true,
    showAllVersions = false,
    dadosFiliacao_federacaoNome = null,
    dadosFiliacao_vinculadoAEntidadeNome = null
  ) => {
    try {
      const conditions = [];

      // Filtro principal: busca por palavra no campo searchWords
      conditions.push(where("dadosPessoais.searchWords", "array-contains", word));

      // Aplicar filtros padrão
      if (dadosFiliacao_federacaoNome) {
        conditions.push(where("dadosFiliacao.federacao", "==", dadosFiliacao_federacaoNome));
      }

      if (onlyActive) {
        conditions.push(where("metadados.active", "==", true));
      }

      if (!showAllVersions) {
        conditions.push(where("metadados.successorId", "==", ""));
      }

      if (dadosFiliacao_vinculadoAEntidadeNome && dadosFiliacao_vinculadoAEntidadeNome !== "") {
        conditions.push(where("dadosFiliacao.vinculadoAEntidade", "==", dadosFiliacao_vinculadoAEntidadeNome));
      }

      // Ordenação e limite
      conditions.push(orderBy("dadosPessoais.name", "asc"));
      conditions.push(limit(queryLimit));

      // Executar consulta
      const q = query(collection(db, "praticantes"), ...conditions);
      const querySnapshot = await getDocs(q);

      const results = querySnapshot.docs.map(doc => doc); // Retorna DocumentSnapshot

      return results;

    } catch (error) {
      console.error(`❌ Erro na busca da palavra "${word}":`, error);
      return [];
    }
  }

  searchByMultipleNormalizedTrigrams = async (
    queryTarget,
    searchTerm,
    queryLimit = 200, // Aumentado para 200 para testar se o problema é o limite
    onlyActive = true,
    showAllVersions = false,
    dadosFiliacao_federacaoNome = null,
    dadosFiliacao_vinculadoAEntidadeNome = null,
    lastVisible = null
  ) => {
    try {
      if (!searchTerm || searchTerm.trim().length === 0) {
        return [];
      }

      // Normalizar e dividir o termo de busca em palavras
      const normalizedSearchTerm = searchTerm.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const words = normalizedSearchTerm.toLowerCase()
        .trim()
        .split(/\s+/)
        .filter(word => word.length >= 2); // Filtrar palavras muito pequenas

      // console.log("Palavras extraídas para busca:", words);

      if (words.length === 0) {
        return [];
      }

      // Se só uma palavra, usa busca tradicional otimizada
      if (words.length === 1) {
        return this.searchBySingleNormalizedTrigram(
          queryTarget,
          words[0],
          queryLimit,
          onlyActive,
          showAllVersions,
          dadosFiliacao_federacaoNome,
          dadosFiliacao_vinculadoAEntidadeNome,
          lastVisible
        );
      }

      // Buscar por cada palavra separadamente
      const searchPromises = words.map(word => 
        this.searchBySingleNormalizedTrigram(
          queryTarget,
          word,
          queryLimit * 2, // Aumenta limite individual para garantir cobertura
          onlyActive,
          showAllVersions,
          dadosFiliacao_federacaoNome,
          dadosFiliacao_vinculadoAEntidadeNome
          // Não passa lastVisible para buscas individuais
        )
      );

      // console.log(`Executando ${words.length} buscas paralelas...`);
      const results = await Promise.all(searchPromises);

      // DEBUG: Mostrar resultados de cada busca individual
      // console.log('=== RESULTADOS DAS BUSCAS INDIVIDUAIS ===');
      // results.forEach((result, index) => {
      //   console.log(`📋 Busca "${words[index]}": ${result.length} documentos encontrados`);
      //   
      //   // Verificar se Fernando está nesta busca
      //   const fernandoEncontrado = result.find(doc => doc.id === 'TRhRghIYvkTs5igtOt6YnoQGAvC3');
      //   if (fernandoEncontrado) {
      //     console.log(`   🎯 FERNANDO ENCONTRADO na busca "${words[index]}"!`);
      //   } else {
      //     console.log(`   ❌ Fernando NÃO encontrado na busca "${words[index]}"`);
      //   }
      //   
      //   result.forEach((doc, docIndex) => {
      //     const data = doc.data();
      //     const nome = data?.dadosPessoais?.name || 'Nome não disponível';
      //     const destaque = doc.id === 'TRhRghIYvkTs5igtOt6YnoQGAvC3' ? ' ⭐ FERNANDO' : '';
      //     console.log(`   ${docIndex + 1}. ID: ${doc.id} - Nome: ${nome}${destaque}`);
      //   });
      //   console.log(''); // linha em branco
      // });

      // Fazer interseção dos resultados baseada no ID do documento
      let intersection = [];
      
      if (results.length > 0 && results[0].length > 0) {
        // Começar com os resultados da primeira palavra
        intersection = results[0];
        // console.log(`🔄 Iniciando interseção com ${intersection.length} documentos da palavra "${words[0]}"`);
        
        // DEBUG: Verificar se Fernando está na primeira busca
        // const fernandoNaPrimeira = intersection.find(doc => doc.id === 'TRhRghIYvkTs5igtOt6YnoQGAvC3');
        // console.log(`   📋 Fernando na primeira busca "${words[0]}": ${fernandoNaPrimeira ? 'SIM ✅' : 'NÃO ❌'}`);
        
        // Para cada palavra subsequente, filtrar apenas documentos que também existem nessa busca
        for (let i = 1; i < results.length; i++) {
          const currentIds = new Set(results[i].map(doc => doc.id));
          
          // DEBUG: Verificar se Fernando está na busca atual
          // const fernandoNaAtual = results[i].find(doc => doc.id === 'TRhRghIYvkTs5igtOt6YnoQGAvC3');
          // console.log(`   📋 Fernando na busca "${words[i]}": ${fernandoNaAtual ? 'SIM ✅' : 'NÃO ❌'}`);
          // console.log(`   🔍 IDs na busca "${words[i]}": ${Array.from(currentIds).slice(0, 5).join(', ')}...`);
          
          // DEBUG: Verificar especificamente o ID do Fernando
          // console.log(`   🎯 ID do Fernando (TRhRghIYvkTs5igtOt6YnoQGAvC3) presente na busca "${words[i]}": ${currentIds.has('TRhRghIYvkTs5igtOt6YnoQGAvC3') ? 'SIM ✅' : 'NÃO ❌'}`);
          
          intersection = intersection.filter(doc => currentIds.has(doc.id));
          
          // console.log(`🔄 Interseção com palavra "${words[i]}": ${previousCount} → ${intersection.length} documentos`);
          
          // Mostrar quais documentos restaram
          // if (intersection.length > 0) {
          //   console.log(`   Documentos restantes após interseção:`);
          //   intersection.forEach((doc, docIndex) => {
          //     const data = doc.data();
          //     const nome = data?.dadosPessoais?.name || 'Nome não disponível';
          //     const destaque = doc.id === 'TRhRghIYvkTs5igtOt6YnoQGAvC3' ? ' ⭐ FERNANDO' : '';
          //     console.log(`      ${docIndex + 1}. ${nome} (ID: ${doc.id})${destaque}`);
          //   });
          // } else {
          //   console.log(`   ❌ Nenhum documento sobrou na interseção`);
          // }
          
          // Se não há mais documentos na interseção, pode parar
          if (intersection.length === 0) {
            break;
          }
        }
      }

      // Aplicar limite final e ordenação
      intersection = intersection
        .slice(0, queryLimit)
        .sort((a, b) => {
          const nameA = a.data()?.dadosPessoais?.name || '';
          const nameB = b.data()?.dadosPessoais?.name || '';
          return nameA.localeCompare(nameB);
        });

      return intersection;

    } catch (error) {
      console.error("Erro na busca por múltiplos trigramas:", error);
      throw error;
    }
  };

  /**
   * Busca por trigrama normalizado para uma única palavra
   * @param {string} queryTarget - Campo alvo da busca
   * @param {string} word - Palavra única para buscar
   * @param {number} queryLimit - Limite de resultados
   * @param {boolean} onlyActive - Filtrar apenas registros ativos
   * @param {boolean} showAllVersions - Incluir versões anteriores
   * @param {string} dadosFiliacao_federacaoNome - Filtro por federação
   * @param {string} dadosFiliacao_vinculadoAEntidadeNome - Filtro por entidade
   * @param {Object} lastVisible - Documento para paginação
   * @returns {Promise<Array>} - Array de documentos
   */
  searchBySingleNormalizedTrigram = async (
    queryTarget,
    word,
    queryLimit,
    onlyActive,
    showAllVersions,
    dadosFiliacao_federacaoNome,
    dadosFiliacao_vinculadoAEntidadeNome,
    lastVisible = null
  ) => {
    try {
      // Gerar trigramas da palavra
      const wordTrigrams = Array.from(generateNormalizedTrigam(word));
      
      // Preparar condições base
      let conditions = [];
      
      // Buscar usando array-contains-any com os trigramas da palavra
      if (wordTrigrams.length > 0) {
        // TESTE DIRETO: Vamos fazer uma consulta mais simples primeiro
        const testTrigram = wordTrigrams[0]; // Pegar o primeiro trigrama
        
        try {
          const testQuery = query(
            collection(db, "praticantes"),
            where("dadosPessoais.normalizedtrigam", "array-contains", testTrigram),
            where("metadados.successorId", "==", ""), // FILTRO CRÍTICO: apenas versões atuais
            limit(5)
          );
          const testSnapshot = await getDocs(testQuery);
          
          if (testSnapshot.docs.length > 0) {
            // Documentos encontrados - processo silencioso para produção
          }
        } catch (testError) {
          // Erro silenciado para produção
        }
        
        // ⚠️ MUDANÇA TEMPORÁRIA: Usar array-contains ao invés de array-contains-any
        const firstTrigram = wordTrigrams[0]; // Usar só o primeiro trigrama
        conditions.push(where("dadosPessoais.normalizedtrigam", "array-contains", firstTrigram));
        // console.log(`   🔍 Consulta principal array-contains com trigrama: "${firstTrigram}"`);
      }

      // TEMPORÁRIO: Aplicando apenas filtros essenciais
      // console.log(`🚨 DEBUG: Aplicando apenas filtros essenciais...`);
      
      // FILTRO CRÍTICO: Apenas versões atuais (não versões antigas)
      conditions.push(where("metadados.successorId", "==", ""));
      // console.log(`   ✅ Filtro successorId aplicado para buscar apenas versões atuais`);

      // Ordenação e limite
      conditions.push(orderBy("dadosPessoais.name", "asc"));
      conditions.push(limit(queryLimit));
      
      // Paginação se necessário
      if (lastVisible) {
        conditions.push(startAfter(lastVisible));
      }

      // Executar consulta
      const querySnapshot = await getDocs(query(collection(db, "praticantes"), ...conditions));
      
      // console.log(`📊 Busca por palavra "${word}": ${querySnapshot.docs.length} documentos encontrados`);
      
      // DEBUG: Mostrar nomes dos documentos encontrados
      // if (querySnapshot.docs.length > 0) {
      //   console.log(`   Nomes encontrados para "${word}":`);
      //   querySnapshot.docs.forEach((doc, index) => {
      //     const data = doc.data();
      //     const nome = data?.dadosPessoais?.name || 'Nome não disponível';
      //     console.log(`      ${index + 1}. ${nome} (ID: ${doc.id})`);
      //   });
      // } else {
      //   console.log(`   ❌ Nenhum documento encontrado para a palavra "${word}"`);
      // }
      
      return querySnapshot.docs;

    } catch (error) {
      console.error(`Erro na busca por trigrama da palavra "${word}":`, error);
      throw error;
    }
  };

  /**
   * MÉTODOS ESPECÍFICOS PARA SearchPessoaComponentVersion5
   * Estes métodos sempre usam normalizedTrigram para nomes (sem % ou $) 
   * e busca direta para CPF/CNPJ - não alteram os métodos da Version4
   */

  /**
   * Busca unificada Version5: sempre trigrama para nomes, busca direta para CPF/CNPJ
   * @param {string} queryTarget - Campo alvo (dadosPessoais.cpf, dadosPessoais.cnpj ou dadosPessoais.normalizedtrigam)
   * @param {string} searchTerm - Termo de busca
   * @param {number} queryLimit - Limite de resultados  
   * @param {boolean} onlyActive - Filtrar apenas ativos
   * @param {boolean} showAllVersions - Incluir versões anteriores
   * @param {string} dadosFiliacao_federacaoNome - Filtro federação
   * @param {string} dadosFiliacao_vinculadoAEntidadeNome - Filtro entidade
   * @returns {Promise<Array>} - Documentos encontrados
   */
  searchVersion5WithVinculo = async (
    queryTarget,
    searchTerm,
    queryLimit,
    onlyActive,
    showAllVersions,
    dadosFiliacao_federacaoNome,
    dadosFiliacao_vinculadoAEntidadeNome
  ) => {
    // console.log("searchVersion5WithVinculo - Parâmetros:", {
    //   queryTarget,
    //   searchTerm,
    //   queryLimit,
    //   onlyActive,
    //   showAllVersions,
    //   dadosFiliacao_federacaoNome,
    //   dadosFiliacao_vinculadoAEntidadeNome
    // });

    try {
      // Se busca por trigrama normalizado, usar busca por múltiplas palavras
      if (queryTarget === "dadosPessoais.normalizedtrigam") {
        return this.searchByMultipleNormalizedTrigrams(
          queryTarget,
          searchTerm,
          queryLimit,
          onlyActive,
          showAllVersions,
          dadosFiliacao_federacaoNome,
          dadosFiliacao_vinculadoAEntidadeNome
        );
      }

      // Para CPF/CNPJ, usar busca tradicional direta
      let conditions = [];
      
      // Busca exata para CPF/CNPJ
      if (searchTerm.length > 0) {
        conditions.push(where(queryTarget, "==", searchTerm));
      }

      // Filtros padrão
      if (dadosFiliacao_federacaoNome) {
        conditions.push(where("dadosFiliacao.federacao", "==", dadosFiliacao_federacaoNome));
      }

      if (onlyActive) {
        conditions.push(where("metadados.active", "==", true));
      }

      if (!showAllVersions) {
        conditions.push(where("metadados.successorId", "==", ""));
      }

      if (dadosFiliacao_vinculadoAEntidadeNome && dadosFiliacao_vinculadoAEntidadeNome !== "") {
        conditions.push(where("dadosFiliacao.vinculadoAEntidade", "==", dadosFiliacao_vinculadoAEntidadeNome));
      }

      // Ordenação e limite
      conditions.push(orderBy(queryTarget, "asc"));
      conditions.push(limit(queryLimit));

      // console.log("searchVersion5WithVinculo - Condições CPF/CNPJ:", conditions);

      // Executar consulta
      const querySnapshot = await getDocs(query(collection(db, "praticantes"), ...conditions));

      // console.log(`Busca Version5 com vínculo por "${searchTerm}": ${querySnapshot.docs.length} documentos`);

      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          uid: doc.id,
          name: data.dadosPessoais?.name?.toUpperCase() || '',
          maxGraduacao: data.dadosTaekwondo?.maxGraduacao || 1,
          birthdate: data.dadosPessoais?.birthdate || '',
          sexo: data.dadosPessoais?.sexo || '',
          logradouroMunicipio: data.dadosEndereco?.logradouroMunicipio || '',
          logradouroUF: data.dadosEndereco?.logradouroUF || '',
          vinculadoAEntidade: data.dadosFiliacao?.vinculadoAEntidade || '',
          vinculadoAEntidadeID: data.dadosFiliacao?.vinculadoAEntidadeID || '',
          federacao: data.dadosFiliacao?.federacao || '',
          mestre: data.dadosTaekwondo?.mestre || '',
          mestreID: data.dadosTaekwondo?.mestreID || '',
          professor: data.dadosTaekwondo?.professor || '',
          professorID: data.dadosTaekwondo?.professorID || '',
          federativeactive: data.metadados?.federativeactive || '',
          active: data.metadados?.active || '',
          urlImagemID: data.dadosPessoais?.urlImagemID || '',
        };
      });

    } catch (error) {
      console.error("Erro na busca Version5 com vínculo:", error);
      throw error;
    }
  };

  /**
   * Busca unificada Version5 SEM vínculo: sempre trigrama para nomes, busca direta para CPF/CNPJ  
   * @param {string} queryTarget - Campo alvo
   * @param {string} searchTerm - Termo de busca
   * @param {number} queryLimit - Limite de resultados
   * @param {boolean} onlyActive - Filtrar apenas ativos
   * @param {boolean} showAllVersions - Incluir versões anteriores
   * @param {string} dadosFiliacao_federacaoNome - Filtro federação
   * @param {string} entidadeFilter - Filtro para entidade específica
   * @returns {Promise<Array>} - Documentos encontrados
   */
  searchVersion5SemVinculo = async (
    queryTarget,
    searchTerm,
    queryLimit,
    onlyActive,
    showAllVersions,
    dadosFiliacao_federacaoNome,
    entidadeFilter = null
  ) => {
    console.log("searchVersion5SemVinculo - Parâmetros:", {
      queryTarget,
      searchTerm,
      queryLimit,
      onlyActive,
      showAllVersions,
      dadosFiliacao_federacaoNome,
      entidadeFilter
    });

    try {
      // Se busca por trigrama normalizado, usar busca por múltiplas palavras
      if (queryTarget === "dadosPessoais.normalizedtrigam") {
        return this.searchByMultipleNormalizedTrigrams(
          queryTarget,
          searchTerm,
          queryLimit,
          onlyActive,
          showAllVersions,
          dadosFiliacao_federacaoNome,
          null // sem vínculo de entidade
        );
      }

      // Para CPF/CNPJ, usar busca tradicional direta
      let conditions = [];
      
      // Busca exata para CPF/CNPJ
      if (searchTerm.length > 0) {
        conditions.push(where(queryTarget, "==", searchTerm));
      }

      // Filtros padrão
      if (dadosFiliacao_federacaoNome) {
        conditions.push(where("dadosFiliacao.federacao", "==", dadosFiliacao_federacaoNome));
      }

      if (onlyActive) {
        conditions.push(where("metadados.active", "==", true));
      }

      if (!showAllVersions) {
        conditions.push(where("metadados.successorId", "==", ""));
      }

      // Filtro específico para entidade (quando aplicável)
      if (entidadeFilter && entidadeFilter !== "") {
        conditions.push(where("dadosFiliacao.vinculadoAEntidade", "==", entidadeFilter));
      }

      // Ordenação e limite
      conditions.push(orderBy(queryTarget, "asc"));
      conditions.push(limit(queryLimit));

      // console.log("searchVersion5SemVinculo - Condições CPF/CNPJ:", conditions);

      // Executar consulta
      const querySnapshot = await getDocs(query(collection(db, "praticantes"), ...conditions));

      // console.log(`Busca Version5 sem vínculo por "${searchTerm}": ${querySnapshot.docs.length} documentos`);

      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          uid: doc.id,
          name: data.dadosPessoais?.name?.toUpperCase() || '',
          maxGraduacao: data.dadosTaekwondo?.maxGraduacao || 1,
          birthdate: data.dadosPessoais?.birthdate || '',
          sexo: data.dadosPessoais?.sexo || '',
          logradouroMunicipio: data.dadosEndereco?.logradouroMunicipio || '',
          logradouroUF: data.dadosEndereco?.logradouroUF || '',
          vinculadoAEntidade: data.dadosFiliacao?.vinculadoAEntidade || '',
          vinculadoAEntidadeID: data.dadosFiliacao?.vinculadoAEntidadeID || '',
          federacao: data.dadosFiliacao?.federacao || '',
          mestre: data.dadosTaekwondo?.mestre || '',
          mestreID: data.dadosTaekwondo?.mestreID || '',
          professor: data.dadosTaekwondo?.professor || '',
          professorID: data.dadosTaekwondo?.professorID || '',
          federativeactive: data.metadados?.federativeactive || '',
          active: data.metadados?.active || '',
          urlImagemID: data.dadosPessoais?.urlImagemID || '',
        };
      });

    } catch (error) {
      console.error("Erro na busca Version5 sem vínculo:", error);
      throw error;
    }
  };

  /**
   * Busca praticantes por nome para AtletaQuickSearch
   * Usa o mesmo sistema eficiente do AutoCompletePlus com searchableName
   * Retorna estrutura compatível com componente de busca de atletas
   * 
   * @param {string} nome - Nome parcial ou completo para buscar
   * @returns {Promise<Array>} Array de atletas com estrutura simplificada
   */
  buscarPorNome = async (nome, options = {}) => {
    try {
      if (!nome || nome.length < 2) {
        return [];
      }

      // ✅ HOTFIX 04/03/2026: Permitir ignorar filtro de bloqueio.bloqueado para eventos estaduais
      const { filtrarBloqueados = true } = options;

      // Normalizar termo de busca (mesma estratégia do AutoCompletePlus)
      // Mesma normalização usada na ESCRITA de searchableName — se as duas
      // divergirem, quem tem acento no nome fica inalcançável.
      const searchTerm = normalizarSearchableName(nome);
      
      // Range query: >= searchTerm e <= searchTerm + '\uf8ff'
      const endText = searchTerm + '\uf8ff';
      
      // ⚠️ REMOVIDO: query "TESTE" de diagnóstico que lia até 100 docs sem filtro de
      // bloqueio e descartava o resultado (`testSnapshot` nunca era usado) — dobrava
      // as leituras de toda busca por nome sem nenhum efeito.

      // Construir query com filtros de bloqueio
      // NOTA: metadados.defiliado e metadados.bloqueio.bloqueado removidos da query
      // pois são undefined na maioria dos docs - serão filtrados em memória
      const conditions = [
        where("dadosPessoais.searchableName", ">=", searchTerm),
        where("dadosPessoais.searchableName", "<=", endText),
        where("metadados.successorId", "==", ""),
        where("metadados.bloqueioAdministrativo", "==", false),
        where("metadados.cancelado", "==", false),
        orderBy("dadosPessoais.searchableName", "asc"),
        limit(100)
      ];
      
      // Executar consulta
      const querySnapshot = await getDocs(query(collection(db, "praticantes"), ...conditions));
      
      // Filtrar em memória por relevância, defiliado e bloqueio.bloqueado
      const filtered = querySnapshot.docs.filter(doc => {
        const data = doc.data();
        // `name` conserva o acento e o termo já veio normalizado — comparar os dois
        // crus descartaria justamente os acentuados que a consulta trouxe.
        const nomeCompleto = normalizarSearchableName(data.dadosPessoais?.name);
        
        // Filtro 1: Nome contém o termo
        if (!nomeCompleto.includes(searchTerm)) {
          return false;
        }
        
        // Filtro 2: Bloqueio.bloqueado (trata undefined como false)
        // ✅ HOTFIX 04/03/2026: Para eventos estaduais, filtrarBloqueados=false — ignorar este filtro
        if (filtrarBloqueados && data.metadados?.bloqueio?.bloqueado === true) {
          return false;
        }
        
        // Filtro 3: Defiliado (trata undefined como false)
        if (data.metadados?.defiliado === true) {
          return false;
        }
        
        return true;
      });
      
      // Mapear para estrutura esperada
      return filtered.map(doc => this.mapearParaAtletaQuickSearch(doc));
      
    } catch (error) {
      console.error("[buscarPorNome] Erro ao buscar atletas:", error);
      console.error("[buscarPorNome] Stack trace:", error.stack);
      throw error;
    }
  };

  /**
   * Mapeia documento do Firestore para estrutura do AtletaQuickSearch
   */
  mapearParaAtletaQuickSearch = (doc) => {
    const data = doc.data();
    
    // Determinar graduação atual
    const maxGraduacao = data.dadosTaekwondo?.maxGraduacao || 1;
    
    // Calcular idade (apenas ano, conforme regulamento - ignora mês e dia)
    let idade = null;
    if (data.dadosPessoais?.birthdate) {
      try {
        let birthDate;
        
        // Verifica se é um Timestamp do Firebase
        if (data.dadosPessoais.birthdate.toDate && typeof data.dadosPessoais.birthdate.toDate === 'function') {
          birthDate = data.dadosPessoais.birthdate.toDate();
        } 
        // Verifica se tem seconds (formato Timestamp)
        else if (data.dadosPessoais.birthdate.seconds) {
          birthDate = new Date(data.dadosPessoais.birthdate.seconds * 1000);
        } 
        // Tenta converter diretamente
        else {
          birthDate = new Date(data.dadosPessoais.birthdate);
        }
        
        const today = new Date();
        
        // Verifica se a data é válida
        if (!isNaN(birthDate.getTime())) {
          idade = today.getFullYear() - birthDate.getFullYear();
          
          // Verifica se a idade calculada é válida
          if (idade < 0 || idade > 120) {
            idade = null;
          }
        }
      } catch (error) {
        // Silenciosamente ignora erros de cálculo de idade
      }
    }
    
    // Determinar classe e formato de graduação
    let classe = 'Iniciante';
    let graduacaoFormatada = '';
    
    if (maxGraduacao >= 11) {
      // Faixas Pretas: graduação 11-19 = 1º-9º Dan
      classe = 'Faixa Preta';
      const dan = maxGraduacao - 10;
      graduacaoFormatada = `${dan}º Dan`;
    } else {
      // Faixas Coloridas: graduação 1-10 = 10º-1º Gub
      const gub = 11 - maxGraduacao; // grad 1=10ºGub, grad 10=1ºGub
      graduacaoFormatada = `${gub}º Gub`;
      
      if (maxGraduacao >= 8) {
        classe = 'Avançado';
      } else if (maxGraduacao >= 5) {
        classe = 'Intermediário';
      }
    }
    
    return {
      id: doc.id,
      dadosPessoais: {
        nome: data.dadosPessoais?.name?.toUpperCase() || '',
        sexo: data.dadosPessoais?.sexo || '', // Não usar default para identificar dados faltantes
        birthdate: data.dadosPessoais?.birthdate || '',
        cpf: data.dadosPessoais?.cpf || '',
        idade: idade // Adiciona idade calculada
      },
      dadosTecnicos: {
        classe: classe,
        graduacao: graduacaoFormatada,
        maxGraduacao: maxGraduacao,
        // Valor CRU do cadastro (0 = sem graduação): o `maxGraduacao` acima tem
        // `|| 1` para exibir e filtrar, e não pode ir para `inscritos`.
        maxGraduacaoCadastro: data.dadosTaekwondo?.maxGraduacao || 0
      },
      dadosFiliacao: {
        federacao: data.dadosFiliacao?.federacao || '',
        // federacaoID é usado pelos filtros de evento (federacoesFiltradas) e pela
        // escolha do pagador "Federação" no wizard de lote — não pode ser omitido.
        federacaoID: data.dadosFiliacao?.federacaoID || '',
        vinculadoAEntidade: data.dadosFiliacao?.vinculadoAEntidade || '',
        vinculadoAEntidadeID: data.dadosFiliacao?.vinculadoAEntidadeID || ''
      },
      dadosEndereco: {
        municipio: data.dadosEndereco?.logradouroMunicipio || '',
        uf: data.dadosEndereco?.logradouroUF || ''
      },
      // Situação cadastral: necessária para bloquear a inscrição de praticante com
      // anuidade em aberto já no momento da seleção, sem uma segunda leitura do
      // documento (ver eventos/helpers/verificarPendenciasAnuidade.js).
      // `buscarPorNome` NÃO filtra por `active` de propósito — o inativo deve
      // aparecer na busca (mestre/professor precisa encontrá-lo); quem decide
      // barrar é o fluxo que consome o resultado.
      // ⚠️ Repassar os valores CRUS, sem `|| false`: se o campo não existir no
      // cadastro, ele precisa continuar `undefined` para o consumidor tratar como
      // indeterminado e reler o documento, em vez de assumir "inativo".
      metadados: {
        active: data.metadados?.active,
        federativeactive: data.metadados?.federativeactive
      }
    };
  };

}

export default new praticantesService();
